import { readMonitorBody, monitorTargets } from "./session-monitor"
import { renderMonitorPage } from "./ui/pages/monitor"
import { renderActivityPage } from "./ui/pages/activity"
import { renderWorkflowEditorPage } from "./ui/pages/workflow-editor"
import { createServer, type IncomingMessage, type ServerResponse } from "http"
import { appendFileSync, existsSync, mkdirSync } from "fs"
import { readFile } from "fs/promises"
import { fileURLToPath } from "url"
import { dirname, resolve } from "path"
import type { DaemonConfig } from "./config"
import { dashboardTokenForNode } from "./mesh-auth"
import { loadOperatorKey } from "@/requests/operator"
import { currentPeople, handleMemberRequest } from "./member-routes"
import { MemberStore } from "@/members/store"
import { forgeLink } from "@/members/work"
import type { BoardConfig, BoardColumn } from "@/boards/config"
import { deriveStage, transitionDiff } from "@/boards/config"
import type { WorkSource, WorkItem } from "@/business/work-pool"
import { GitLabWorkSource } from "@/business/work-pool"
import { SORTABLE_JS } from "./vendor/sortable"
import { handleWizardGet, handleWizardPost, handleStartDaemonPost, wizardState } from "./setup-wizard"
import { handleAdminGet, handleAdminApi, handleAdminConfigGet } from "./admin-panel"
import { readVoiceHealth } from "@/voice/voice-health"
import { handleGraphGet, handleGraphApi } from "./graph-panel"
import { handleApprovalsPageGet, handleApprovalsPanelApi } from "./approvals-panel"
import { handlePeoplePanel } from "./people-panel"
import { handleObservabilityGet, handleObservabilityApi } from "./observability-panel"
import { handleLedgerApi, renderLedgerPage } from "./ledger-panel"
import { renderCostPage } from "./ui/pages/cost"
import { renderProjectsPage } from "./ui/pages/projects"
import { createWikiHandler } from "@/wiki/serve"
import { recordSurfaceUse } from "@/observability/surface-usage"
import { handleActivityGraphApi, handleActivityGraphStream, handleActivityGraphDetail, setDaemonConfigForActivityGraph, buildLocalActivityGraphSnapshot, mergeFleetSnapshots, withForgeStatus, type FleetSnapshot } from "./activity-graph-panel"
import { handleAgentPageGet, handleAgentApi } from "./agent-panel"
import { renderLivePage } from "./ui/pages/live"
import { renderMeshPage } from "./ui/pages/mesh"
import { renderGuestsPage } from "./ui/pages/guests"
import { fetchMeshAnalytics, fetchMeshDay, proxyNodeAnalytics, type NodeTarget } from "./mesh-analytics-api"
import { renderBoardsPage } from "./ui/pages/boards"
import { renderGlossaryPage } from "./ui/pages/glossary"
import { renderWorkflowsPage } from "./ui/pages/workflows"
import { renderProceduresPage } from "./ui/pages/procedures"
import { renderProcessesPage } from "./ui/pages/processes"
import { renderTaskPage } from "./ui/pages/task"
import { renderHistoryPage } from "./ui/pages/history"
import { handleWorkflowsApi } from "./workflows-api"
import { ROUTINE_LIMITS, type Routine } from "./routines"
import { LayoutStore, RunStore, WorkflowStore, type WorkflowRun } from "@/workflows"
import { TokenStore, recordHasScope, extractToken, type TokenRecord } from "./token-store"
import { handleAppRequest } from "./app-routes"
import { dashboardIcon } from "./app-icon"
import { assetLinks, handleDashboardPlaces, placesDeps, type PlacesDeps } from "./app-places"
import { daemonFireDeps } from "@/places/fire"
import { renderPlacesPage } from "./ui/pages/places"
import { relayedPush, relayedPushHere, type AppPushDeps } from "./app-push"
import { appAnnounceDeps } from "./app-announce"
import { PushStore } from "@/channels/push-store"
import { AppChatStore } from "./app-chat-store"
import { appPresence, type AppChatDeps, type AppMeshPeer } from "./app-chat"
import { AppCallbackPuller, type CallbackEvent, type CallbackReplyBody } from "./app-chat-callbacks"
import type { AppVoiceDeps } from "./app-voice"
import type { AppCameraDeps } from "./app-camera"
import { pushKeysPath, readPushKeys } from "@/channels/push-keys"
import { openDb } from "@/storage/sqlite"
import { logSlowQueries } from "@/storage/slow-queries"
import type { AppFleetDeps, ApprovalItem, NodeApprovals } from "./app-fleet"
import { decide, listInbox, type InboxItem } from "@/approvals/inbox"
import { readApprovalSettings } from "@/approvals/settings"
import { classifyBrowserRequest, isStateChangingOrPreflight } from "./browser-origin"
import { headerBuild, setTopbarFeatures, type TopbarPeer } from "./topbar"

// --- Kanban Board Dashboard ---
//
// Zero-build web UI, served on its own port (default 4202, bound 127.0.0.1).
// Inline HTML/CSS/JS, manual routing, no framework.
//
// Column kinds (see src/boards/config.ts):
//   - open-backlog: opened issues with no Status::* scoped label
//   - scoped-label: opened issues with a specific Status::X label
//   - closed:       closed issues (bounded by closedWindowDays)
//   - label:        opened issues with a flat mapsToLabel (legacy)
//
// Drag-drop produces a ColumnTransition that may: add/remove labels, close,
// or reopen an issue (via GitLab `state_event`).

/** The database the pages read, with any query that holds this process for
 *  200 ms or longer named in the log (#448): one such query stops every page. */
function dashboardDb(): ReturnType<typeof openDb> {
  const db = openDb()
  if (db) logSlowQueries(db, "dashboard")
  return db
}

export function startBoardDashboard(config: DaemonConfig): void {
  const dashboard = config.dashboard
  const boards = config.boards

  const port = dashboard.port
  const bind = dashboard.bind
  const token = dashboard.token

  const sources = new Map<string, WorkSource>()
  for (const board of boards) {
    try {
      sources.set(board.id, buildWorkSource(board, config))
    } catch (e: any) {
      console.error(`  Board "${board.id}" failed to initialize: ${e.message}`)
    }
  }

  // Workflow stores are lightweight (filesystem-backed, no network). Created
  // once per dashboard so the /workflows page + editor API don't re-scan
  // directories on every request.
  //
  // Home-node id comes from the mesh config when available; falls back to a
  // stable "local" so single-node runs work out of the box. Until the
  // integration seam commits, the dispatcher itself isn't booted in the
  // daemon — the editor's view over these stores is read/write, but the
  // engine's run-time dispatch + mesh forwarding aren't wired yet.
  const wfNodeId = config.node?.id || "local"
  const wfDir = config.workflows?.dir ? resolve(process.cwd(), config.workflows.dir) : undefined
  const workflowStore = new WorkflowStore(wfDir ? { baseDir: wfDir } : undefined)
  const workflowRuns = new RunStore(wfDir ? { baseDir: wfDir, nodeId: wfNodeId } : { nodeId: wfNodeId })
  const workflowLayouts = new LayoutStore(wfDir ? { baseDir: wfDir } : undefined)

  // Activity-graph snapshot needs agent metadata (tier, model, name) from
  // agentx.json — set the module-level reference so the snapshot builder can
  // read it when serving each request.
  setDaemonConfigForActivityGraph(config)

  // Opened before the first page is served: every later reader in this
  // process shares this handle, so its slow queries are logged too.
  dashboardDb()

  // Compact primary nav. Every other surface stays routable by URL.
  setTopbarFeatures({
    business: config.business?.enabled === true,
  })

  const server = createServer(async (req, res) => {
    try {
      await handleBoardRequest(req, res, { boards, sources, token, config, workflowStore, workflowRuns, workflowLayouts })
    } catch (e: any) {
      sendJson(res, 500, { error: e.message || "internal error" })
    }
  })

  // #277 — replies an agent writes after a delegation, filed in the phone
  // conversation they belong to (app-chat-callbacks.ts).
  appCallbackPuller(config).start()

  server.listen(port, bind, () => {
    const displayHost = bind === "0.0.0.0" ? "localhost" : bind
    if (boards.length === 0) {
      console.log(`\n  Live dashboard: http://${displayHost}:${port}\n`)
      console.log("  (no boards configured — '/' serves the live view; add a board with 'agentx board add')")
    } else {
      console.log(`\n  Kanban dashboard: http://${displayHost}:${port}\n`)
      console.log(`  Boards: ${boards.map((b) => b.id).join(", ")}`)
    }
    if (bind === "127.0.0.1" && !token) {
      console.log("  (bound to localhost; no auth token — safe default)")
    } else if (bind !== "127.0.0.1" && !token) {
      console.log("  ⚠ bound to a non-loopback address without a token — writes are UNAUTHENTICATED")
    }
  })
}

/** Every page a human can land on. Kept explicit rather than inferred from
 *  "has no file extension" so assets, redirects and API routes can never
 *  drift into the numbers the reduction decisions are made from. */
const DASHBOARD_PAGES = new Set([
  "/",
  "/live",
  "/mesh",
  "/monitor",
  "/activity",
  "/boards",
  "/glossary",
  "/workflows",
  "/workflows/editor",
  "/procedures",
  "/processes",
  "/graph",
  "/setup",
  "/admin",
  "/admin/graph",
  "/approvals",
  "/people",
  "/places",
  "/admin/health",
  "/admin/observability",
  "/admin/ledger",
  "/admin/cost",
  "/admin/projects",
  "/admin/wiki",
])

export interface Ctx {
  boards: BoardConfig[]
  sources: Map<string, WorkSource>
  token?: string
  config: DaemonConfig
  workflowStore: WorkflowStore
  workflowRuns: RunStore
  workflowLayouts: LayoutStore
}

/** Exported for tests only; production requests come through startBoardDashboard. */
export async function handleBoardRequest(req: IncomingMessage, res: ServerResponse, ctx: Ctx): Promise<void> {
  const url = new URL(req.url || "/", "http://localhost")
  const path = url.pathname
  const method = (req.method || "GET").toUpperCase()

  // Every route below matches `path`, which has dot segments folded and
  // backslashes read as slashes. A proxy that publishes only some paths
  // (a teammate's /member and /api/member, say) may forward the address as
  // it was typed, so "/member/../approvals" would arrive here as
  // "/approvals". Only an address sent in its normal form is served (#452).
  if ((req.url || "/").split(/[?#]/)[0] !== path) {
    sendJson(res, 404, { error: "not found" })
    return
  }

  // No CORS headers: every dashboard page calls this server on its own
  // origin (browser-origin.ts). A page on another origin, localhost on
  // another port included, can't read responses, can't pass a preflight,
  // and its writes stop here: the X-Requested-With check below only holds
  // while no foreign page is allowed to send that header.
  if (classifyBrowserRequest(req.headers) === "foreign" && isStateChangingOrPreflight(method)) {
    sendJson(res, 403, { error: "Forbidden: request from a page on another origin" })
    return
  }
  if (method === "OPTIONS") { res.writeHead(204); res.end(); return }

  // Phone app. First, above every proxy and the loopback-trusting gates
  // below: /app and /api/app/* always need a device token (app-routes.ts).
  // A teammate's own work page: /member and /api/member/* (member-routes.ts).
  // Same rule as the phone app: a machine's own key or nothing.
  if (await handleMemberRequest(req, res, path, method, {
    nodeName: ctx.config.node?.name, root: process.cwd(), people: () => currentPeople(ctx.config.people),
    members: membersStore(ctx.config.members.logRetentionDays),
    db: () => dashboardDb(), linkFor: (channel, chatId) => forgeLink(channel, chatId, { gitlab: ctx.config.channels.gitlab?.host }),
  })) return
  if (await handleAppRequest(req, res, path, method, { nodeName: ctx.config.node?.name, fleet: appFleetDeps(ctx.config), push: appPushDeps(ctx.config), announce: appAnnounceDeps(ctx.config), chat: appChatDeps(ctx.config), voice: appVoiceDeps(ctx.config), camera: appCameraDeps(ctx.config), places: appPlacesDeps(ctx.config), assetLinks: () => assetLinks(ctx.config) })) return

  // The AX symbol as favicon and touch icon. Public: browsers fetch icons
  // without credentials, and they hold no data.
  const icon = method === "GET" ? dashboardIcon(path) : null
  if (icon) {
    res.writeHead(200, { "Content-Type": icon.type, "Cache-Control": "public, max-age=86400" })
    res.end(icon.body)
    return
  }

  // Count which dashboard pages operators actually open. Page paths only —
  // no query strings, no ids, and nothing under /api (those are XHR from a
  // page we already counted, so they'd inflate every number).
  // See docs/architecture/surface-reduction.md.
  if (method === "GET" && DASHBOARD_PAGES.has(path)) {
    recordSurfaceUse("page", path)
  }

  if (method === "GET" && path === "/") {
    // Home is Live, always. It answers the question an operator actually
    // opens the dashboard for — who is alive and what are they doing.
    // Boards used to take the root whenever any were configured, which meant
    // the landing page silently changed identity based on config; now they
    // live at /boards like every other surface.
    //
    // The one exception is a machine with no agents yet, where there is
    // nothing live to show and the wizard is the only useful destination.
    const wz = wizardState()
    if (!wz.configExists || wz.agentCount === 0) {
      res.writeHead(302, { Location: "/setup" })
      res.end()
      return
    }
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderLivePage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  if (method === "GET" && path === "/boards") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderBoardsPage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  if (method === "GET" && path === "/live") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderLivePage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  // n8n posts here. It is forwarded to the local node rather than served
  // from the dashboard so the operator has ONE address to paste into n8n —
  // the one they are already looking at — instead of learning which port the
  // daemon happens to be on.
  const n8nIn = method === "POST" && path.match(/^\/webhook\/n8n\/[A-Za-z0-9._:-]{1,64}$/)
  if (n8nIn) {
    try {
      const [node] = await resolveNodeTargets(ctx.config)
      if (!node) { sendJson(res, 503, { error: "no node available" }); return }
      const body = await new Promise<string>(resolve => {
        let d = ""; req.on("data", c => (d += c)); req.on("end", () => resolve(d)); req.on("error", () => resolve(""))
      })
      const r = await fetch(node.url + path, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(node.token ? { Authorization: `Bearer ${node.token}` } : {}) },
        body: body || "{}",
      })
      res.writeHead(r.status, { "Content-Type": "application/json" })
      res.end(await r.text())
    } catch (e: any) {
      sendJson(res, 502, { error: "could not reach the node", message: String(e?.message || e) })
    }
    return
  }

  // Workflow-builder chat (proxies to main daemon where the dispatcher
  // + AgentRegistry live). The board-dashboard serves /workflows/editor
  // but runs its own workflow stores; the chat endpoint needs the
  // running agent registry, which only the main daemon has.
  // Ask-an-agent drawer. Every verb proxies to one node: `node` picks which,
  // and a conversation belongs to the node that holds it.
  if (path === "/api/assistant" || path.startsWith("/api/assistant/")) {
    try {
      const targets = await resolveNodeTargets(ctx.config)
      const wanted = method === "POST" ? undefined : url.searchParams.get("node")
      const body: any = method === "POST" ? await readJson(req) : undefined
      const pick = body?.node ?? wanted
      const node = pick ? targets.find(n => n.url === pick) : targets[0]
      if (!node) { sendJson(res, 400, { error: "unknown node" }); return }
      const qs = url.search ? url.search.replace(/([?&])node=[^&]*/, "$1").replace(/[?&]$/, "") : ""
      const r = await fetch(node.url + path + qs, {
        method,
        headers: { "Content-Type": "application/json", ...(node.token ? { Authorization: `Bearer ${node.token}` } : {}) },
        ...(method === "POST" ? { body: JSON.stringify({ ...body, node: undefined }) } : {}),
      })
      sendJson(res, r.status, await r.json().catch(() => ({ error: `HTTP ${r.status}` })))
    } catch (e: any) {
      sendJson(res, 502, { error: "could not reach the node", message: e?.message || String(e) })
    }
    return
  }

  if (method === "POST" && path === "/api/workflows/editor/chat") {
    try {
      const body = await readJson(req)
      const headers: Record<string, string> = { "Content-Type": "application/json" }
      if (ctx.config.dashboard.token) headers["Authorization"] = `Bearer ${ctx.config.dashboard.token}`
      const daemonUrl = ctx.config.dashboard.daemonUrl.replace(/\/+$/, "")
      const r = await fetch(`${daemonUrl}/api/workflows/editor/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify(body ?? {}),
      })
      const data = await r.json().catch(() => ({ error: `HTTP ${r.status}` }))
      sendJson(res, r.status, data)
    } catch (e: any) {
      sendJson(res, 502, { error: "daemon unreachable", message: e.message || String(e) })
    }
    return
  }

  if (method === "GET" && path === "/api/n8n/workflows") {
    try {
      const [node] = await resolveNodeTargets(ctx.config)
      if (!node) { sendJson(res, 200, { configured: false, workflows: [] }); return }
      const r = await fetch(node.url + "/api/n8n/workflows", {
        headers: node.token ? { Authorization: `Bearer ${node.token}` } : {},
      })
      sendJson(res, r.status, await r.json().catch(() => ({ configured: false, workflows: [] })))
    } catch {
      sendJson(res, 200, { configured: false, workflows: [] })
    }
    return
  }

  if (method === "GET" && path === "/workflows/editor") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderWorkflowEditorPage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  if (method === "GET" && path === "/activity") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderActivityPage({ peers: buildTopbarPeers(ctx.config), view: url.searchParams.get("view") }))
    return
  }
  if (method === "GET" && path === "/monitor") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderMonitorPage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  if (path === "/api/monitor" || path.startsWith("/api/monitor/")) {
    const ac = new AbortController()
    const timeout = setTimeout(() => ac.abort(), 8000)
    try {
      const targets = monitorTargets(await resolveNodeTargets(ctx.config, ac.signal), ctx.config.mesh.peers)
      if (method === "GET" && path === "/api/monitor") {
        const nodes = await Promise.all(targets.map(async node => {
          try {
            const r = await fetch(node.url + "/monitor", { headers: node.token ? { Authorization: `Bearer ${node.token}` } : {}, signal: ac.signal })
            if (!r.ok) throw new Error(`HTTP ${r.status}`)
            return { name: node.name, url: node.url, ok: true, data: await r.json() }
          } catch (e: any) { return { name: node.name, url: node.url, ok: false, error: e.message } }
        }))
        sendJson(res, 200, { nodes }); return
      }
      const node = targets.find(n => n.url === url.searchParams.get("node"))
      if (!node) { sendJson(res, 400, { error: "Select a known node" }); return }
      const op = path.slice("/api/monitor/".length)
      if (!((method === "GET" && ["discover", "actions", "activity"].includes(op)) || (method === "POST" && ["register", "ended", "action", "retry", "clear"].includes(op)))) {
        sendJson(res, 405, { error: "Unknown monitor operation" }); return
      }
      const body = method === "POST" ? JSON.stringify(await readMonitorBody(req)) : undefined
      const r = await fetch(node.url + "/monitor/" + op + (op === "actions" ? "?offset=" + encodeURIComponent(url.searchParams.get("offset") || "0")
        : op === "activity" ? "?hours=" + encodeURIComponent(url.searchParams.get("hours") || "24") : ""), { method, body, signal: ac.signal, headers: { "Content-Type": "application/json", ...(node.token ? { Authorization: `Bearer ${node.token}` } : {}) } })
      sendJson(res, r.status, await r.json())
    } catch (e: any) { sendJson(res, 502, { error: e.message }) }
    finally { clearTimeout(timeout) }
    return
  }
  if (method === "GET" && path === "/mesh") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderMeshPage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  // Places for the phone's place reminders (#676): the same store and
  // routes the phone app uses (app-places.ts), from the dashboard.
  if (method === "GET" && path === "/places") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderPlacesPage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  // Guest meshes (#380): the host's panel, with its data proxied to the
  // daemon's /mesh/guests (loopback-trusted there, like the other panels).
  if (method === "GET" && path === "/guests") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderGuestsPage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  if ((method === "GET" && path === "/api/guests") || (method === "POST" && /^\/api\/guests\/[^/]+\/(pause|resume|end|update)$/.test(path))) {
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" }
      if (ctx.config.dashboard.token) headers["Authorization"] = `Bearer ${ctx.config.dashboard.token}`
      const daemonUrl = ctx.config.dashboard.daemonUrl.replace(/\/+$/, "")
      const body = method === "POST" ? await readJson(req).catch(() => ({})) : undefined
      const r = await fetch(`${daemonUrl}${path.replace(/^\/api\/guests/, "/mesh/guests")}`, {
        method, headers, ...(body !== undefined ? { body: JSON.stringify(body ?? {}) } : {}),
      })
      sendJson(res, r.status, await r.json().catch(() => ({ error: `HTTP ${r.status}` })))
    } catch (e: any) {
      sendJson(res, 502, { error: "daemon unreachable", message: e.message || String(e) })
    }
    return
  }
  if (method === "GET" && path === "/glossary") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderGlossaryPage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  if (method === "GET" && path === "/workflows") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderWorkflowsPage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  if (method === "GET" && path === "/procedures") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderProceduresPage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  if (method === "GET" && path === "/processes") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderProcessesPage({}))
    return
  }
  // /tasks/:id — watching an agent work is a place, not a popup. Replaces the
  // full-screen modal on Live and the right-hand drawer on the agent page,
  // both of which streamed this same task into something you couldn't link
  // to, reload, or keep open beside anything else.
  const taskPage = method === "GET" && path.match(/^\/tasks\/([^/]+)$/)
  if (taskPage) {
    const agentId = url.searchParams.get("agent") || ""
    const nodeUrl = url.searchParams.get("node") || ctx.config.dashboard.daemonUrl
    if (!agentId) {
      res.writeHead(400, { "Content-Type": "text/html; charset=utf-8" })
      res.end("<p>Missing <code>?agent=</code>. Open this task from the Live page.</p>")
      return
    }
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderTaskPage({
      taskId: decodeURIComponent(taskPage[1]),
      agentId,
      agentName: url.searchParams.get("name") || undefined,
      channel: url.searchParams.get("channel") || undefined,
      archived: url.searchParams.get("archived") === "1",
      ask: url.searchParams.get("ask") || undefined,
      askAt: url.searchParams.get("at") || undefined,
      nodeUrl,
      peers: buildTopbarPeers(ctx.config),
    }))
    return
  }
  // /agents/:id/history — was a 360px drawer sliding over Live. The list is
  // what you came to read, it wants width, and every row leads somewhere, so
  // it needs to be linkable and back-navigable.
  const histPage = method === "GET" && path.match(/^\/agents\/([^/]+)\/history$/)
  if (histPage) {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderHistoryPage({
      agentId: decodeURIComponent(histPage[1]),
      agentName: url.searchParams.get("name") || undefined,
      nodeUrl: url.searchParams.get("node") || ctx.config.dashboard.daemonUrl,
      peers: buildTopbarPeers(ctx.config),
    }))
    return
  }
  // /graph is the canonical doc path; the page itself lives at /admin/graph.
  if (method === "GET" && path === "/graph") {
    res.writeHead(302, { Location: "/admin/graph" })
    res.end()
    return
  }

  // Setup wizard — form-driven first-run experience for non-technical operators.
  if (method === "GET" && path === "/setup") {
    handleWizardGet(req, res)
    return
  }
  if (method === "POST" && path === "/api/setup") {
    await handleWizardPost(req, res)
    return
  }
  if (method === "POST" && path === "/api/setup/start-daemon") {
    await handleStartDaemonPost(req, res)
    return
  }

  // Admin panel — ongoing management without editing JSON.
  if (method === "GET" && path === "/admin") {
    handleAdminGet(req, res, buildTopbarPeers(ctx.config), ctx.token)
    return
  }
  // /approvals — one inbox for every decision waiting for the operator.
  if (method === "GET" && path === "/approvals") {
    handleApprovalsPageGet(res, buildTopbarPeers(ctx.config), ctx.token)
    return
  }
  // /admin/graph — Intent Knowledge Graph: pending approvals + taxonomy editor.
  if (method === "GET" && path === "/admin/graph") {
    handleGraphGet(req, res, buildTopbarPeers(ctx.config), ctx.token)
    return
  }
  // /admin/health — platform-health view over route_traces + rotations + errors.
  // Renamed from /admin/observability — that name suggested business flow,
  // but the page only ever covered platform health. Old URL redirects so
  // existing bookmarks keep working.
  if (method === "GET" && (path === "/admin/health" || path === "/admin/observability")) {
    if (path === "/admin/observability") {
      res.writeHead(302, { Location: "/admin/health" })
      res.end()
      return
    }
    handleObservabilityGet(req, res, buildTopbarPeers(ctx.config))
    return
  }
  // ── Cross-mesh API proxy (must precede every /api/admin/* handler) ──
  // When the topbar peer selector is set to a non-primary peer, the
  // client-side fetch wrapper attaches X-Agentx-Peer / ?peer=<url> to the
  // request. Forward the whole call to that peer's dashboard before any
  // local handler picks it up, so the activity-graph + observability +
  // every other admin surface follow the selector.
  //
  // Special peerId="fleet" → fan-out + merge across local + every
  // dashboard.daemons[] entry. Only meaningful for the activity-graph
  // snapshot endpoint right now; other endpoints fall through to local
  // until they grow merge semantics.
  // /api/workflows + /api/agents live outside /api/admin/ for legacy
  // reasons but are part of the admin surface and follow the same
  // peer-selector contract. Mirror of the shouldProxy() allowlist on
  // the client side (topbar.ts). Treat them the same as /api/admin/*
  // so admin pages proxy to the chosen peer when the selector is set.
  if (path.startsWith("/api/admin/") || path.startsWith("/api/workflows") || path.startsWith("/api/agents")) {
    // An explicit ?peer=fleet (the /activity map) beats the topbar's
    // per-node header: that view is mesh-wide by definition.
    const peerId = url.searchParams.get("peer") === "fleet" ? "fleet"
      : String(req.headers["x-agentx-peer"] || "").trim() || url.searchParams.get("peer") || ""
    if (peerId === "fleet") {
      if (path === "/api/admin/activity-graph") {
        await handleActivityGraphFleet(req, res, ctx)
        return
      }
      if (path === "/api/admin/activity-graph/stream") {
        await handleActivityGraphStream(req, res, path, (hours) => buildFleetActivitySnapshot(hours, ctx.config))
        return
      }
      // Dispatch detail in fleet mode: the id was rewritten to
      // "<nodeName>::<originalId>" by the merger so React keys stay
      // unique. Route the lookup back to the node that owns the row.
      const detailMatch = path.match(/^\/api\/admin\/activity-graph\/dispatch\/(.+)$/)
      if (detailMatch) {
        const decoded = decodeURIComponent(detailMatch[1])
        const sep = decoded.indexOf("::")
        if (sep > 0) {
          const nodeName = decoded.slice(0, sep)
          const realId = decoded.slice(sep + 2)
          const localNodeName = ctx.config.node?.id || ctx.config.node?.name || "local"
          if (nodeName === localNodeName) {
            // Local row — strip the prefix and let the local handler answer.
            req.url = `/api/admin/activity-graph/dispatch/${encodeURIComponent(realId)}`
            // Fall through to the regular handler below.
          } else {
            const peerByName = (ctx.config.dashboard?.daemons || []).find((d) => (d.name || d.url) === nodeName)
            if (peerByName) {
              const peer = findPeer(peerByName.url.replace(/\/+$/, ""), ctx.config)
              if (peer) {
                await proxyAdminToPeer(
                  req, res, peer,
                  `/api/admin/activity-graph/dispatch/${encodeURIComponent(realId)}`,
                )
                return
              }
            }
            sendJson(res, 404, { error: `unknown node "${nodeName}" in fleet dispatch id` })
            return
          }
        }
      }
      // Anything else in fleet mode → answer locally.
    } else if (peerId && peerId !== "primary") {
      const peer = findPeer(peerId, ctx.config)
      if (!peer) { sendJson(res, 404, { error: `unknown peer: ${peerId}` }); return }
      // Strip ?peer= so the destination doesn't re-proxy.
      const stripped = (url.search || "").replace(/[?&]peer=[^&]*/g, (m) => m.startsWith("?") ? "?" : "")
      const cleanQuery = stripped === "?" ? "" : stripped
      await proxyAdminToPeer(req, res, peer, path + cleanQuery)
      return
    }
  }

  if (method === "GET" && path.startsWith("/api/admin/observability/")) {
    if (await handleObservabilityApi(req, res, path)) return
  }
  // /api/admin/logs/stream — SSE tail of the daemon's stdout. Mirrors
  // `agentx daemon logs -f`. On systemd-managed nodes this calls
  // journalctl; on others it falls back to /tmp/agentx-daemon.log.
  if (method === "GET" && path === "/api/admin/logs/stream") {
    await streamDaemonLogs(req, res)
    return
  }
  // /api/admin/doctor — preflight check JSON. Same shape as
  // `agentx doctor --json`, served in-process.
  if (method === "GET" && path === "/api/admin/doctor") {
    try {
      const { runDoctorChecks } = await import("@/commands/doctor")
      const url = new URL(req.url || "/", "http://_")
      const running = url.searchParams.get("running") !== "false"
      const result = await runDoctorChecks({ running })
      sendJson(res, 200, result)
    } catch (e: any) {
      sendJson(res, 500, { error: e?.message || String(e) })
    }
    return
  }
  // /admin/ledger — read-only window over .agentx/intent/ledger.sqlite.
  // Mirrors `agentx ledger stats/events/divergences/active`. Replay and
  // lineage stay CLI-only.
  if (method === "GET" && path === "/admin/ledger") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderLedgerPage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  if (method === "GET" && path.startsWith("/api/admin/ledger/")) {
    if (await handleLedgerApi(req, res, path)) return
  }
  // /admin/cost — token-spend page. Replaces the standalone
  // `agentx usage serve` (port 4201) and the Cost tab inside Health.
  if (method === "GET" && path === "/admin/cost") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderCostPage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  // /admin/projects — aggregated per-project view. Data via
  // GET /api/admin/projects on the daemon, proxied by the existing
  // /api/admin/* path-prefix proxy so the mesh selector works.
  if (method === "GET" && path === "/admin/projects") {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(renderProjectsPage({ peers: buildTopbarPeers(ctx.config) }))
    return
  }
  // /admin/wiki/* — wiki UI mounted natively into the dashboard. Same
  // renderer as `agentx wiki serve`, no second port required. The wiki
  // hub instance is created lazily on first request and reused for the
  // lifetime of the dashboard process; updates from `wiki absorb` are
  // picked up because each render rebuilds its index from disk.
  if (method === "GET" && (path === "/admin/wiki" || path.startsWith("/admin/wiki/"))) {
    if (path === "/admin/wiki") {
      // Trailing slash so internal links stay relative to the mount.
      res.writeHead(302, { Location: "/admin/wiki/" })
      res.end()
      return
    }
    await getOrCreateWikiHandler(ctx)(req, res)
    return
  }
  // /admin/activity-graph retired: its map lives on as the Map view of
  // /activity. The APIs below still feed that view.
  if (method === "GET" && path === "/admin/activity-graph") {
    res.writeHead(302, { Location: "/activity?view=map" })
    res.end()
    return
  }
  if (method === "GET" && path === "/api/admin/activity-graph") {
    if (await handleActivityGraphApi(req, res, path)) return
  }
  if (method === "GET" && path.startsWith("/api/admin/activity-graph/stream")) {
    if (await handleActivityGraphStream(req, res, path)) return
  }
  if (method === "GET" && path.startsWith("/api/admin/activity-graph/dispatch/")) {
    if (await handleActivityGraphDetail(req, res, path)) return
  }
  // /admin/agents/<id> — dedicated per-agent page with md editor + skill mgr.
  const agentPageMatch = method === "GET" && path.match(/^\/admin\/agents\/([a-z0-9][a-z0-9_-]*)$/)
  if (agentPageMatch) {
    handleAgentPageGet(req, res, agentPageMatch[1], buildTopbarPeers(ctx.config), ctx.token)
    return
  }
  // (Cross-mesh proxy is now handled at the top of /api/admin/*. The
  // local-token enforcement below still runs for any admin call that
  // didn't match a specific handler above.)
  if (path.startsWith("/api/admin/")) {
    // Local admin calls: enforce dashboard.token (if configured) BEFORE the
    // dispatcher runs. The legacy ctx.token check further down only catches
    // paths nothing else handled — admin routes were silently bypassing it.
    if (ctx.token) {
      const authHeader = req.headers.authorization || ""
      const got = authHeader.startsWith("Bearer ") ? authHeader.slice(7) : ""
      if (got !== ctx.token) {
        const tokStore = new TokenStore()
        const rec = got ? tokStore.verify(got) : null
        if (!rec || !recordHasScope(rec, "dashboard:write")) {
          sendJson(res, 401, { error: "unauthorized" })
          return
        }
      }
    }
  }
  if (method === "GET" && path === "/api/admin/config") {
    await handleAdminConfigGet(req, res)
    return
  }
  // Configured voices macOS took away, as the daemon last found them
  // (src/voice/voice-health.ts); after the token gate above.
  if (method === "GET" && path === "/api/admin/voice-health") {
    sendJson(res, 200, readVoiceHealth() ?? { checkedAt: null, missing: [] })
    return
  }
  if (path.startsWith("/api/admin/approvals")) {
    await handleApprovalsPanelApi(req, res, path, url)
    return
  }
  // People (#441): the page and its /api/admin/people data, after the token
  // gate above. The people list is read per request, like the member page.
  if (await handlePeoplePanel(req, res, path, {
    people: () => currentPeople(ctx.config.people), members: membersStore(ctx.config.members.logRetentionDays),
    root: process.cwd(), db: () => dashboardDb(), peers: buildTopbarPeers(ctx.config), localToken: ctx.token,
  })) return
  if (path.startsWith("/api/admin/graph/")) {
    await handleGraphApi(req, res, path)
    return
  }
  if (path.startsWith("/api/admin/agent/")) {
    await handleAgentApi(req, res, path)
    return
  }
  if (path.startsWith("/api/admin/")) {
    await handleAdminApi(req, res, path)
    return
  }
  if (method === "GET" && path === "/sortable.min.js") {
    res.writeHead(200, { "Content-Type": "application/javascript; charset=utf-8", "Cache-Control": "public, max-age=86400" })
    res.end(SORTABLE_JS)
    return
  }

  // Serve the web-bundled editor artifact. Built by `tsup --config tsup.web.config.ts`
  // to dist/web/<name>.js. Any `/assets/<name>` request is mapped
  // 1:1 into dist/web so future bundles (graph editor, ...) can live there
  // without another route registration.
  if (method === "GET" && path.startsWith("/assets/")) {
    const rel = path.slice("/assets/".length)
    // Allow dotted stems (e.g. "activity-graph.global.js") but keep the
    // whitelist narrow to script/map/style files.
    if (!/^[a-zA-Z0-9._-]+\.(js|map|css)$/.test(rel)) {
      sendJson(res, 400, { error: "invalid asset name" }); return
    }
    const assetPath = resolveFromHere("web/" + rel)
    try {
      const buf = await readAsset(assetPath)
      const ct = rel.endsWith(".map") ? "application/json; charset=utf-8"
        : rel.endsWith(".css") ? "text/css; charset=utf-8"
        : "application/javascript; charset=utf-8"
      // A hashed URL names exactly one build, so it can be cached hard; a bare
      // one must keep revalidating because its bytes change under it.
      const immutable = url.searchParams.has("v")
      res.writeHead(200, {
        "Content-Type": ct,
        "Cache-Control": immutable ? "public, max-age=31536000, immutable" : "public, max-age=60",
      })
      res.end(buf)
    } catch {
      sendJson(res, 404, { error: "asset not found", hint: "run: npm run build:web" })
    }
    return
  }

  // Live mesh view endpoints (JSON snapshot + SSE push every 2s)
  if (method === "GET" && path === "/api/live") {
    try {
      const snap = await buildLiveSnapshot(ctx.config)
      sendJson(res, 200, snap)
    } catch (e: any) { sendJson(res, 502, { error: e.message }) }
    return
  }
  if (method === "GET" && path === "/api/mesh") {
    try {
      const date = url.searchParams.get("date") || new Date().toISOString().slice(0, 10)
      const timezone = url.searchParams.get("timezone") || "UTC"
      const snap = await buildLiveSnapshot(ctx.config, { date, timezone })
      sendJson(res, 200, snap)
    } catch (e: any) { sendJson(res, 502, { error: e.message }) }
    return
  }

  // Mesh analytics — fleet-wide activity, failure causes, zombie jobs and
  // thread lifetimes. Fans out to every node's /analytics/mesh and merges.
  //   GET /api/mesh/analytics?days=30&tzOffset=<minutes>&limit=60
  if (method === "GET" && path === "/api/mesh/analytics") {
    const num = (k: string, d: number, lo: number, hi: number): number => {
      const v = parseInt(url.searchParams.get(k) || "", 10)
      return Number.isFinite(v) ? Math.max(lo, Math.min(hi, v)) : d
    }
    try {
      const targets = await resolveNodeTargets(ctx.config)
      sendJson(res, 200, await fetchMeshAnalytics(targets, {
        days: num("days", 30, 0, 180),
        tzOffsetMinutes: num("tzOffset", 0, -840, 840),
        limit: num("limit", 60, 1, 200),
      }))
    } catch (e: any) { sendJson(res, 502, { error: e.message }) }
    return
  }

  // One activity column, across the fleet.
  //   GET /api/mesh/day?day=YYYY-MM-DD&tzOffset=<minutes>&limit=40
  if (method === "GET" && path === "/api/mesh/day") {
    const day = url.searchParams.get("day") || ""
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
      sendJson(res, 400, { error: "day=YYYY-MM-DD query param required" }); return
    }
    const num = (k: string, d: number, lo: number, hi: number): number => {
      const v = parseInt(url.searchParams.get(k) || "", 10)
      return Number.isFinite(v) ? Math.max(lo, Math.min(hi, v)) : d
    }
    try {
      const targets = await resolveNodeTargets(ctx.config)
      sendJson(res, 200, await fetchMeshDay(targets, {
        day,
        tzOffsetMinutes: num("tzOffset", 0, -840, 840),
        limit: num("limit", 40, 1, 200),
      }))
    } catch (e: any) { sendJson(res, 502, { error: e.message }) }
    return
  }

  // Drill-down proxies. The node URL comes from the merged analytics
  // payload and is re-checked against the allowlist before forwarding.
  //   GET /api/mesh/thread?node=&agent=&channel=&chat=&limit=
  //   GET /api/mesh/job?node=&kind=cron|workflow&key=&limit=
  //   GET /api/mesh/run?node=&task=
  //   GET /api/mesh/conversation?node=&agent=&channel=&chat=&tzOffset=
  if (method === "GET" && (path === "/api/mesh/thread" || path === "/api/mesh/job"
      || path === "/api/mesh/run" || path === "/api/mesh/conversation")) {
    const nodeUrl = url.searchParams.get("node")
    if (!nodeUrl) { sendJson(res, 400, { error: "node query param required" }); return }
    const q = url.searchParams
    let upstream: string
    if (path === "/api/mesh/thread") {
      const [agent, channel, chat] = [q.get("agent"), q.get("channel"), q.get("chat")]
      if (!agent || !channel || !chat) { sendJson(res, 400, { error: "agent, channel, chat required" }); return }
      upstream = `/analytics/thread?agent=${encodeURIComponent(agent)}&channel=${encodeURIComponent(channel)}`
        + `&chat=${encodeURIComponent(chat)}&limit=${encodeURIComponent(q.get("limit") || "120")}`
    } else if (path === "/api/mesh/job") {
      const [kind, key] = [q.get("kind"), q.get("key")]
      if (!kind || !key) { sendJson(res, 400, { error: "kind and key required" }); return }
      upstream = `/analytics/job?kind=${encodeURIComponent(kind)}&key=${encodeURIComponent(key)}`
        + `&limit=${encodeURIComponent(q.get("limit") || "120")}`
    } else if (path === "/api/mesh/conversation") {
      const [agent, channel, chat] = [q.get("agent"), q.get("channel"), q.get("chat")]
      if (!agent || !channel || !chat) { sendJson(res, 400, { error: "agent, channel, chat required" }); return }
      upstream = `/analytics/conversation?agent=${encodeURIComponent(agent)}&channel=${encodeURIComponent(channel)}`
        + `&chat=${encodeURIComponent(chat)}&tzOffset=${encodeURIComponent(q.get("tzOffset") || "0")}`
    } else {
      const task = q.get("task")
      if (!task) { sendJson(res, 400, { error: "task required" }); return }
      upstream = `/analytics/run/${encodeURIComponent(task)}`
    }
    try {
      await proxyNodeAnalytics(res, await resolveNodeTargets(ctx.config), nodeUrl, upstream)
    } catch (e: any) { sendJson(res, 502, { error: e.message }) }
    return
  }
  if (method === "GET" && path === "/api/live/stream") {
    startLiveStream(req, res, ctx.config)
    return
  }

  // Cross-node SSE proxy for a single running task. Browser stays on
  // the dashboard origin; we connect back to the originating daemon URL.
  // GET /api/task/stream?node=<daemonUrl>&agent=<id>&task=<tid>
  if (method === "GET" && path === "/api/task/stream") {
    const u = new URL(req.url || "/", "http://localhost")
    const nodeUrl = u.searchParams.get("node")
    const agentId = u.searchParams.get("agent")
    const taskId = u.searchParams.get("task")
    if (!nodeUrl || !agentId || !taskId) {
      sendJson(res, 400, { error: "node, agent, task query params required" })
      return
    }
    await proxyTaskStream(req, res, ctx, nodeUrl, agentId, taskId)
    return
  }

  // Cross-node JSON proxy for the persisted task history (Recent Activities panel).
  // GET /api/task/history?node=<url>&agent=<id>&limit=N      → list
  // GET /api/task/history?node=<url>&agent=<id>&task=<tid>   → single record
  if (method === "GET" && path === "/api/task/history") {
    const u = new URL(req.url || "/", "http://localhost")
    const nodeUrl = u.searchParams.get("node")
    const agentId = u.searchParams.get("agent")
    const taskId = u.searchParams.get("task")
    const limit = u.searchParams.get("limit") || "50"
    if (!nodeUrl || !agentId) {
      sendJson(res, 400, { error: "node and agent query params required" })
      return
    }
    await proxyTaskHistory(res, ctx, nodeUrl, agentId, taskId, limit)
    return
  }

  // Cross-node action proxy: stop / follow-up a running task on any daemon
  // in the dashboard's allowlist. The browser talks to the dashboard origin
  // (same as /api/task/stream), and the dashboard forwards to the daemon's
  // /api/tasks/:taskId/{cancel|followup} endpoint with the operator token.
  //   POST /api/task/action?node=<url>&task=<id>&kind=cancel|followup
  // body: passthrough JSON (cancel: { reason? }; followup: { message, replace?, sender? })
  if (method === "POST" && path === "/api/task/action") {
    const u = new URL(req.url || "/", "http://localhost")
    const nodeUrl = u.searchParams.get("node")
    const taskId = u.searchParams.get("task")
    const kind = u.searchParams.get("kind")
    if (!nodeUrl || !taskId || (kind !== "cancel" && kind !== "followup")) {
      sendJson(res, 400, { error: "node, task, kind=cancel|followup query params required" })
      return
    }
    await proxyTaskAction(req, res, ctx, nodeUrl, taskId, kind)
    return
  }

  // Stop whatever is speaking or showing on a node's screen: a lesson, a
  // talk, narration (the daemon's POST /voice/stop).
  //   POST /api/voice/stop?node=<url>
  if (method === "POST" && path === "/api/voice/stop") {
    const nodeUrl = new URL(req.url || "/", "http://localhost").searchParams.get("node")
    if (!nodeUrl) { sendJson(res, 400, { error: "node query param required" }); return }
    await proxyNodePost(req, res, ctx, nodeUrl, "/voice/stop")
    return
  }

  // Agent roster (proxy to configured daemon) — used by draft-agent picker.
  if (method === "GET" && path === "/api/agents") {
    try {
      const headers: Record<string, string> = {}
      if (ctx.config.dashboard.token) headers["Authorization"] = `Bearer ${ctx.config.dashboard.token}`
      const r = await fetch(ctx.config.dashboard.daemonUrl.replace(/\/+$/, "") + "/agents", { headers })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const list = await r.json()
      sendJson(res, 200, list)
    } catch (e: any) { sendJson(res, 502, { error: e.message || "agents fetch failed" }) }
    return
  }

  // Business status (proxy to configured daemon) — used by the boards
  // Activity panel, which used to call the daemon cross-origin.
  if (method === "GET" && path === "/api/business/status") {
    try {
      const headers: Record<string, string> = {}
      if (ctx.config.dashboard.token) headers["Authorization"] = `Bearer ${ctx.config.dashboard.token}`
      const r = await fetch(ctx.config.dashboard.daemonUrl.replace(/\/+$/, "") + "/business/status", { headers })
      if (!r.ok) { sendJson(res, r.status, { error: `HTTP ${r.status}` }); return }
      sendJson(res, 200, await r.json())
    } catch (e: any) { sendJson(res, 502, { error: e.message || "business status fetch failed" }) }
    return
  }

  // AI-assisted issue draft — proxies to daemon /task with convention-aware prompt.
  if (method === "POST" && path === "/api/draft") {
    const body = await readJson(req)
    try {
      const drafted = await draftIssue(ctx.config, body)
      sendJson(res, 200, drafted)
    } catch (e: any) { sendJson(res, 502, { error: e.message || "draft failed" }) }
    return
  }

  // --- Public agent endpoint (always token-gated, regardless of dashboard.token) ---
  const publicAgentMatch = path.match(/^\/api\/public\/agents\/([^/]+)\/messages$/)
  if (publicAgentMatch && method === "POST") {
    const agentId = publicAgentMatch[1]
    const tokenRec = requireScopedToken(req, res, [`agent:${agentId}`])
    if (!tokenRec) return
    await proxyPublicAgentMessage(req, res, ctx.config, agentId)
    return
  }

  // The build the primary daemon is running and since when, for the header
  // of every page (#465). The dashboard is its own process, so it asks.
  // Above the /api/* token gate on purpose: the header script has no token
  // to send, and putting one in the top bar would put it on every page.
  // Only headerBuild's five fields leave here, which the daemon's own
  // /health already gives without a token.
  //   GET /api/node/build
  if (method === "GET" && path === "/api/node/build") {
    try {
      const headers: Record<string, string> = {}
      if (ctx.config.dashboard.token) headers["Authorization"] = `Bearer ${ctx.config.dashboard.token}`
      const r = await fetch(ctx.config.dashboard.daemonUrl.replace(/\/+$/, "") + "/health", { headers, signal: AbortSignal.timeout(3000) })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      sendJson(res, 200, headerBuild(await r.json()))
    } catch (e: any) { sendJson(res, 502, { error: e.message || "daemon not reachable" }) }
    return
  }

  if (path.startsWith("/api/") && ctx.token) {
    // Legacy: if dashboard.token is configured, every /api/* request must
    // carry it (or a scoped token with dashboard:write).
    const authHeader = req.headers.authorization || ""
    const got = authHeader.startsWith("Bearer ") ? authHeader.slice(7) : ""
    if (got === ctx.token) { /* fall through */ }
    else {
      const tokStore = new TokenStore()
      const rec = got ? tokStore.verify(got) : null
      if (!rec || !recordHasScope(rec, "dashboard:write")) {
        sendJson(res, 401, { error: "unauthorized" })
        return
      }
    }
  }

  // Mesh feed (#166) — the newest events on the primary daemon's bus. That
  // bus already holds each mesh peer's own events (src/events/peer-feed.ts),
  // so one read covers the fleet; fanning out like /events would show every
  // peer event twice. Per-step agent activity is left out.
  //
  // Below the /api/* token gate on purpose: the daemon's /events/recent is
  // mesh-gated, and this proxy reads it with the dashboard's credential, so
  // an ungated route here would hand the whole mesh's events to anyone who
  // can reach the dashboard. The daemon accepts MESH_TOKEN (not
  // dashboard.token) off-box, so that is tried first.
  //   GET /api/mesh/feed?limit=50
  if (method === "GET" && path === "/api/mesh/feed") {
    await serveMeshFeed(res, ctx.config, url.searchParams)
    return
  }

  const isWrite = method === "POST" || method === "PATCH" || method === "DELETE" || method === "PUT"
  if (isWrite && path.startsWith("/api/")) {
    const xr = req.headers["x-requested-with"]
    if (xr !== "agentx-board") { sendJson(res, 400, { error: "missing X-Requested-With: agentx-board" }); return }
  }

  // Places (#676), below the /api/* token and X-Requested-With checks:
  // the /places page adds and removes the owner's places here.
  if (await handleDashboardPlaces(req, res, path, method, appPlacesDeps(ctx.config))) return

  // "Restart when idle" on a node's header (Live page). The daemon holds the
  // request and exits by itself once no task is running, so its service
  // manager starts it again; it refuses when none would. Below the token and
  // X-Requested-With checks on purpose: this stops a node.
  //   POST /api/node/restart?node=<url>          { timeoutMinutes?, onTimeout? }
  //   POST /api/node/restart/cancel?node=<url>
  if (method === "POST" && (path === "/api/node/restart" || path === "/api/node/restart/cancel")) {
    const nodeUrl = url.searchParams.get("node")
    if (!nodeUrl) { sendJson(res, 400, { error: "node query param required" }); return }
    await proxyNodePost(req, res, ctx, nodeUrl, path === "/api/node/restart" ? "/daemon/restart" : "/daemon/restart/cancel")
    return
  }

  // Read-through proxies for runs. The dashboard's local RunStore only
  // sees runs home-noded on THIS machine, but the user typically wants
  // the cross-fleet view: runs fired on peer-server when GitLab events
  // land there, runs fired on Mac when local channels fire. We merge
  // the local list with the main-daemon's list and return the union.
  // Fan-out SSE proxy for /events — multiplexes every configured peer
  // daemon's event stream into a single stream. Lets the board-dashboard
  // show live activity across the whole fleet.
  if (method === "GET" && path === "/events") {
    const url = new URL(req.url || "/", "http://localhost")
    const qs = url.searchParams.toString()
    res.writeHead(200, {
      "Content-Type": "text/event-stream",
      "Cache-Control": "no-cache",
      "Connection": "keep-alive",
    })
    const targets: Array<{ name: string; url: string; token?: string }> = [
      { name: ctx.config.node.name || "local", url: ctx.config.dashboard.daemonUrl, token: ctx.config.dashboard.token },
      ...(ctx.config.dashboard.daemons || []).map((d) => ({ name: d.name, url: d.url, token: d.token })),
    ]
    const closed = { flag: false }
    const controllers: AbortController[] = []
    req.on("close", () => { closed.flag = true; for (const c of controllers) try { c.abort() } catch { /* */ } })
    // Heartbeat — some proxies drop idle SSE at 30s.
    const heartbeat = setInterval(() => { if (!closed.flag) { try { res.write(": ping\n\n") } catch { /* */ } } }, 15000)
    req.on("close", () => clearInterval(heartbeat))
    for (const t of targets) {
      void (async () => {
        const headers: Record<string, string> = { Accept: "text/event-stream" }
        if (t.token) headers["Authorization"] = `Bearer ${t.token}`
        const ctrl = new AbortController()
        controllers.push(ctrl)
        try {
          const r = await fetch(`${t.url.replace(/\/+$/, "")}/events${qs ? `?${qs}` : ""}`, { headers, signal: ctrl.signal })
          if (!r.ok || !r.body) return
          const reader = r.body.getReader()
          const dec = new TextDecoder()
          let buf = ""
          while (!closed.flag) {
            const { done, value } = await reader.read()
            if (done) break
            buf += dec.decode(value, { stream: true })
            let idx: number
            while ((idx = buf.indexOf("\n\n")) >= 0) {
              const frame = buf.slice(0, idx)
              buf = buf.slice(idx + 2)
              if (closed.flag) break
              try { res.write(frame + "\n\n") } catch { closed.flag = true }
            }
          }
        } catch { /* ignore — peer disconnected or aborted */ }
      })()
    }
    return
  }

  if (method === "GET" && path === "/api/workflows/runs") {
    try {
      const url = new URL(req.url || "/", "http://localhost")
      const limit = Math.max(1, Math.min(500, Number(url.searchParams.get("limit") || 50)))
      const workflowId = url.searchParams.get("workflowId") || undefined
      // summary=1 strips the per-run context blob (full webhook payloads,
      // per-node outputs — routinely 10-50KB each) and trims history to
      // the last 5 entries. Cuts a 100-run mesh-aggregated response from
      // multi-MB / 30s+ down to ~100KB / 1s. Run-detail (GET /runs/:id)
      // and SSE stream still return the full shape unchanged.
      const summary = url.searchParams.get("summary") === "1" || url.searchParams.get("summary") === "true"
      const local = ctx.workflowRuns.list({ limit, workflowId })
      const qs = `?limit=${limit}${workflowId ? `&workflowId=${encodeURIComponent(workflowId)}` : ""}${summary ? "&summary=1" : ""}`
      // Fan out to every configured peer daemon in parallel. Primary
      // (dashboard.daemonUrl) gets the dashboard.token; entries in
      // dashboard.daemons use their own per-peer token. The summary flag
      // is forwarded so peer daemons also slim their responses (the daemon
      // handler in src/daemon/index.ts honors the same query param).
      const targets: Array<{ url: string; token?: string }> = [
        { url: ctx.config.dashboard.daemonUrl, token: ctx.config.dashboard.token },
        ...(ctx.config.dashboard.daemons || []).map((d) => ({ url: d.url, token: d.token })),
      ]
      const remoteLists = await Promise.all(targets.map(async (t) => {
        const headers: Record<string, string> = {}
        if (t.token) headers["Authorization"] = `Bearer ${t.token}`
        try {
          // 5s timeout per peer — a slow/unhealthy mesh node must not pin
          // the dashboard's first paint forever. AbortController keeps the
          // existing fetch ergonomics; on timeout we just skip this peer's
          // contribution and merge whatever we have.
          const ctrl = new AbortController()
          const timer = setTimeout(() => ctrl.abort(), 5000)
          try {
            const r = await fetch(`${t.url.replace(/\/+$/, "")}/api/workflows/runs${qs}`, { headers, signal: ctrl.signal })
            if (!r.ok) return []
            const data = await r.json() as { runs?: WorkflowRun[] }
            return Array.isArray(data.runs) ? data.runs : []
          } finally {
            clearTimeout(timer)
          }
        } catch { return [] }
      }))
      const byId = new Map<string, WorkflowRun>()
      for (const r of local) byId.set(r.id, r)
      for (const list of remoteLists) for (const r of list) byId.set(r.id, r)
      const merged = Array.from(byId.values()).sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, limit)
      // Ensure the local runs the listing returns are also slimmed; the
      // handleWorkflowsApi path further down does this for its branch but
      // we never reach it because this handler returns first.
      const out = summary ? merged.map((r) => slimRun(r)) : merged
      sendJson(res, 200, { runs: out })
    } catch (e: any) {
      sendJson(res, 502, { error: "runs fetch failed", message: e.message || String(e) })
    }
    return
  }
  const runDetailMatch = method === "GET" && path.match(/^\/api\/workflows\/runs\/([^\/]+)$/)
  if (runDetailMatch) {
    const runId = decodeURIComponent(runDetailMatch[1])
    const local = ctx.workflowRuns.get(runId)
    if (local) { sendJson(res, 200, { run: local }); return }
    // Try every peer until one returns the run. First hit wins.
    const targets: Array<{ url: string; token?: string }> = [
      { url: ctx.config.dashboard.daemonUrl, token: ctx.config.dashboard.token },
      ...(ctx.config.dashboard.daemons || []).map((d) => ({ url: d.url, token: d.token })),
    ]
    for (const t of targets) {
      try {
        const headers: Record<string, string> = {}
        if (t.token) headers["Authorization"] = `Bearer ${t.token}`
        const r = await fetch(`${t.url.replace(/\/+$/, "")}/api/workflows/runs/${encodeURIComponent(runId)}`, { headers })
        if (r.ok) {
          sendJson(res, 200, await r.json())
          return
        }
      } catch { /* try next */ }
    }
    sendJson(res, 404, { error: "run not found on any configured peer" })
    return
  }

  // /api/actions/builtin — proxy GET to the daemon. The /procedures page
  // reads the live built-in catalog from this endpoint; daemon owns the
  // typed registry, dashboard runs in a separate process.
  if (method === "GET" && path === "/api/actions/builtin") {
    try {
      const headers: Record<string, string> = {}
      if (ctx.config.dashboard.token) headers["Authorization"] = `Bearer ${ctx.config.dashboard.token}`
      const daemonUrl = ctx.config.dashboard.daemonUrl.replace(/\/+$/, "")
      const r = await fetch(`${daemonUrl}/api/actions/builtin`, { headers })
      const data = await r.json().catch(() => ({ error: `HTTP ${r.status}` }))
      sendJson(res, r.status, data)
    } catch (e: any) {
      sendJson(res, 502, { error: "daemon unreachable", message: e.message || String(e) })
    }
    return
  }

  // Active-workflow manual run — proxies the daemon's POST /workflows/:id/run
  // endpoint. The /workflows page on the dashboard fires this for the "▶ Run"
  // button. Body shape passed through unchanged: { payload?: object, force?: bool }.
  // The daemon already enforces the trigger.manual constraint and returns
  // 409 when force is needed, which the page surfaces as a toast.
  const runMatch = method === "POST" && path.match(/^\/workflows\/([^/]+)\/run$/)
  if (runMatch) {
    try {
      const body = await readJson(req).catch(() => ({}))
      const headers: Record<string, string> = { "Content-Type": "application/json" }
      if (ctx.config.dashboard.token) headers["Authorization"] = `Bearer ${ctx.config.dashboard.token}`
      const daemonUrl = ctx.config.dashboard.daemonUrl.replace(/\/+$/, "")
      const r = await fetch(`${daemonUrl}/workflows/${encodeURIComponent(runMatch[1])}/run`, {
        method: "POST",
        headers,
        body: JSON.stringify(body ?? {}),
      })
      const data = await r.json().catch(() => ({ error: `HTTP ${r.status}` }))
      sendJson(res, r.status, data)
    } catch (e: any) {
      sendJson(res, 502, { error: "daemon unreachable", message: e.message || String(e) })
    }
    return
  }

  // Draft replay — fires an ad-hoc workflow run on the daemon. Lives on
  // the daemon (not in workflows-api.ts) because it needs the dispatcher

  // Workflow API — read (dashboard:read) / write (dashboard:write). Fine-
  // grained scope checks let read-only scoped tokens still populate the
  // /workflows observability page when dashboard.token isn't configured.
  if (path.startsWith("/api/workflows")) {
    const handled = handleWorkflowsApi(req, res, {
      store: ctx.workflowStore,
      runs: ctx.workflowRuns,
      layouts: ctx.workflowLayouts,
      requireScope: (r, s, scopes) => {
        // When ctx.token is set the legacy check above already verified
        // write scope on any /api call, so we don't re-gate here.
        // When it's not set (localhost default), enforce scopes ourselves
        // only if the caller *presented* a token — otherwise allow through
        // to preserve the loopback "safe default" UX.
        if (ctx.token) return true
        const presented = extractToken(r as any)
        if (!presented) return true
        const rec = new TokenStore().verify(presented)
        if (!rec) { sendJson(s, 401, { error: "invalid or revoked token" }); return false }
        for (const scope of scopes) {
          if (!recordHasScope(rec, scope)) { sendJson(s, 403, { error: `token missing scope: ${scope}`, scopes: rec.scopes }); return false }
        }
        return true
      },
    })
    if (handled) return
  }

  if (method === "GET" && path === "/api/boards") {
    sendJson(res, 200, ctx.boards.map((b) => ({
      id: b.id, name: b.name, source: b.source,
      primaryToolLabel: b.primaryToolLabel, labels: b.labels,
      columns: b.columns, timeRangeDays: b.timeRangeDays,
    })))
    return
  }

  // /api/boards/:id/members — project members (for assignee picker)
  const membersMatch = path.match(/^\/api\/boards\/([a-z0-9_-]+)\/members$/)
  if (membersMatch && method === "GET") {
    const source = ctx.sources.get(membersMatch[1]) as GitLabWorkSource | undefined
    if (!source) { sendJson(res, 404, { error: "unknown board" }); return }
    try {
      const members = typeof source.listMembers === "function" ? await source.listMembers() : []
      sendJson(res, 200, members)
    } catch (e: any) { sendJson(res, 502, { error: e.message }) }
    return
  }

  // /api/boards/:id/milestones — project milestones (for milestone picker)
  const milestonesMatch = path.match(/^\/api\/boards\/([a-z0-9_-]+)\/milestones$/)
  if (milestonesMatch && method === "GET") {
    const source = ctx.sources.get(milestonesMatch[1]) as GitLabWorkSource | undefined
    if (!source) { sendJson(res, 404, { error: "unknown board" }); return }
    try {
      const ms = typeof source.listMilestones === "function" ? await source.listMilestones() : []
      sendJson(res, 200, ms)
    } catch (e: any) { sendJson(res, 502, { error: e.message }) }
    return
  }

  // /api/boards/:id/items[/...]
  // Legacy shape: /items/:itemId/move (move-only). New shape: /items/:itemId (GET/PATCH), /items (POST, GET).
  const moveMatch = path.match(/^\/api\/boards\/([a-z0-9_-]+)\/items\/(.+?)\/move$/)
  const itemOpMatch = path.match(/^\/api\/boards\/([a-z0-9_-]+)\/items(?:\/(.+))?$/)

  if (moveMatch && method === "PATCH") {
    const [, boardId, rawItemId] = moveMatch
    const itemId = decodeURIComponent(rawItemId)
    const board = ctx.boards.find((b) => b.id === boardId)
    if (!board) { sendJson(res, 404, { error: `unknown board: ${boardId}` }); return }
    const source = ctx.sources.get(boardId)
    if (!source) { sendJson(res, 503, { error: `board "${boardId}" source unavailable` }); return }
    const body = await readJson(req)
    const toCol = board.columns.find((c) => c.id === body.to)
    const fromCol = board.columns.find((c) => c.id === body.from)
    if (!toCol) { sendJson(res, 400, { error: `unknown 'to' column: ${body.to}` }); return }
    try {
      await applyTransition(source, itemId, fromCol, toCol)
      audit({ actor: auditActor(req), action: "move", boardId, itemId, payload: { from: body.from, to: body.to } })
      sendJson(res, 200, { ok: true })
    } catch (e: any) { sendJson(res, 502, { error: e.message || "transition failed" }) }
    return
  }

  if (itemOpMatch) {
    const [, boardId, rawItemId] = itemOpMatch
    const itemId = rawItemId ? decodeURIComponent(rawItemId) : undefined
    const board = ctx.boards.find((b) => b.id === boardId)
    if (!board) { sendJson(res, 404, { error: `unknown board: ${boardId}` }); return }
    const source = ctx.sources.get(boardId) as GitLabWorkSource | undefined
    if (!source) { sendJson(res, 503, { error: `board "${boardId}" source unavailable` }); return }

    // GET /items — column dump
    if (method === "GET" && !itemId) {
      const { columns, totals } = await loadBoardColumns(board, source, url.searchParams)
      sendJson(res, 200, { columns, totals })
      return
    }

    // GET /items/:itemId — full detail
    if (method === "GET" && itemId) {
      try {
        const detail = typeof source.getItem === "function" ? await source.getItem(itemId) : null
        if (!detail) { sendJson(res, 404, { error: "not found" }); return }
        sendJson(res, 200, detail)
      } catch (e: any) { sendJson(res, 502, { error: e.message }) }
      return
    }

    // PATCH /items/:itemId — update
    if (method === "PATCH" && itemId) {
      const body = await readJson(req)
      try {
        const updated = typeof source.updateItem === "function"
          ? await source.updateItem(itemId, body)
          : null
        if (!updated) { sendJson(res, 501, { error: "update unsupported" }); return }
        audit({ actor: auditActor(req), action: "update", boardId, itemId, payload: body })
        sendJson(res, 200, updated)
      } catch (e: any) { sendJson(res, 502, { error: e.message }) }
      return
    }

    // POST /items — create new issue
    if (method === "POST" && !itemId) {
      const body = await readJson(req)
      if (!body.title) { sendJson(res, 400, { error: "title required" }); return }
      if (!source.capabilities.create || !source.create) {
        sendJson(res, 400, { error: `source ${source.type} does not support create` }); return
      }
      try {
        const created = await source.create({
          title: body.title,
          description: body.description,
          labels: body.labels,
          assigneeUsernames: body.assigneeUsernames,
          milestoneTitle: body.milestoneTitle,
          projectHint: body.projectHint,
        })
        audit({ actor: auditActor(req), action: "create", boardId, itemId: created.id, payload: { title: body.title } })
        sendJson(res, 201, created)
      } catch (e: any) { sendJson(res, 502, { error: e.message }) }
      return
    }
  }

  // Friendly aliases for naked paths people copy-paste from terminal output,
  // Slack messages, or muscle memory ("just open /health"). The dashboard
  // proper lives under /admin/<page> — unknown top-level paths that match a
  // known admin section get a 302 so the URL works instead of dumping
  // {"error":"not found"} JSON. Only slugs that resolve to a real admin
  // page are mapped — phantom redirects (/admin/<slug> 404) would be worse
  // than the original 404.
  if (method === "GET" && /^\/[a-z][a-z-]*\/?$/.test(path)) {
    const slug = path.replace(/^\/+|\/+$/g, "")
    const SLUG_TO_ADMIN: Record<string, string> = {
      health: "/admin/health",
      ledger: "/admin/ledger",
      cost: "/admin/cost",
      projects: "/admin/projects",
      graph: "/admin/graph",
      wiki: "/admin/wiki/",
      observability: "/admin/health",
    }
    const target = SLUG_TO_ADMIN[slug]
    if (target) {
      res.writeHead(302, { Location: target })
      res.end()
      return
    }
  }

  sendJson(res, 404, { error: "not found", path })
}

// --- Column loading ---

async function loadBoardColumns(
  board: BoardConfig,
  source: WorkSource,
  query: URLSearchParams,
): Promise<{ columns: Record<string, WorkItem[]>; totals: Record<string, number> }> {
  if (!source.capabilities.listAll || !source.listAll) {
    const empty: Record<string, WorkItem[]> = {}
    for (const c of board.columns) empty[c.id] = []
    return { columns: empty, totals: Object.fromEntries(board.columns.map((c) => [c.id, 0])) }
  }

  const baseLabels: string[] = []
  if (board.primaryToolLabel) baseLabels.push(board.primaryToolLabel)
  const extraLabel = query.get("label") || undefined
  if (extraLabel) baseLabels.push(extraLabel)
  const search = query.get("q") || undefined
  const milestone = query.get("milestone") || undefined
  const assignee = query.get("assignee") || undefined
  const openDays = parseInt(query.get("days") || String(board.timeRangeDays), 10)
  const closedDays = parseInt(query.get("closedDays") || String(board.closedWindowDays ?? 30), 10)

  // One query per column kind type. We batch all scoped-label/label/open-backlog
  // columns into a single "opened" query (client-side bucket them), and the
  // closed column (if any) into a second "closed" query.
  const hasOpen = board.columns.some((c) => c.kind !== "closed")
  const hasClosed = board.columns.some((c) => c.kind === "closed")

  const open: WorkItem[] = hasOpen
    ? await source.listAll({
        sinceDays: openDays, labels: baseLabels.length ? baseLabels : undefined,
        search, milestone, state: "opened",
      })
    : []
  const closed: WorkItem[] = hasClosed
    ? await source.listAll({
        sinceDays: closedDays, labels: baseLabels.length ? baseLabels : undefined,
        search, milestone, state: "closed",
      })
    : []

  const columns: Record<string, WorkItem[]> = {}
  for (const c of board.columns) columns[c.id] = []

  // Closed items → the closed column (if any).
  const closedCol = board.columns.find((c) => c.kind === "closed")
  for (const item of closed) {
    if (closedCol) columns[closedCol.id].push({ ...item, stage: closedCol.id })
  }

  // Open items → bucket by deriveStage over the open columns only.
  // Items with a scoped Status::* label that no column claims are dropped
  // (they're off-board workflow states like Status::Done when no Done column exists).
  const openColumns = board.columns.filter((c) => c.kind !== "closed")
  for (const item of open) {
    const stage = deriveStage(item, openColumns)
    if (!stage) continue
    const list = columns[stage]
    if (list) list.push({ ...item, stage })
  }

  // Assignee filter (post-query).
  if (assignee) {
    for (const k of Object.keys(columns)) {
      columns[k] = columns[k].filter((i) => i.assignee === assignee)
    }
  }

  // Deterministic in-column ordering: priority asc, updatedAt desc.
  for (const k of Object.keys(columns)) {
    columns[k].sort((a, b) => {
      const p = (a.priority ?? 99) - (b.priority ?? 99)
      if (p !== 0) return p
      return (b.updatedAt || "").localeCompare(a.updatedAt || "")
    })
  }

  const totals = Object.fromEntries(Object.entries(columns).map(([k, v]) => [k, v.length]))
  return { columns, totals }
}

async function applyTransition(
  source: WorkSource,
  itemId: string,
  fromCol: BoardColumn | undefined,
  toCol: BoardColumn,
): Promise<void> {
  const diff = transitionDiff(fromCol, toCol)
  const gl = source as GitLabWorkSource
  // Close/reopen first (so the issue exists in the right state before label edits).
  if (diff.closeIssue && typeof gl.setState === "function") {
    await gl.setState(itemId, "close")
  } else if (diff.reopen && typeof gl.setState === "function") {
    await gl.setState(itemId, "reopen")
  }
  if ((diff.addLabels?.length || diff.removeLabels?.length) && typeof gl.transitionMany === "function") {
    await gl.transitionMany(itemId, diff.addLabels || [], diff.removeLabels || [])
  } else if (diff.addLabels?.length && source.transition) {
    await source.transition(itemId, diff.addLabels[0], diff.removeLabels?.[0])
  }
}

// --- WorkSource construction ---

/** Trim a WorkflowRun to just the fields the listing UI needs. Drops the
 *  per-run `context` (full webhook payload + per-node outputs, often 10-50KB
 *  each) and clips `history` to the last 5 entries. Both the dashboard's
 *  mesh-aggregation handler and workflows-api.ts call this for `summary=1`
 *  responses; the shape MUST match between the two so the page doesn't see
 *  divergent fields per code path. */
function slimRun(r: WorkflowRun): Partial<WorkflowRun> {
  return {
    id: r.id,
    workflowId: r.workflowId,
    workflowVersion: r.workflowVersion,
    homeNode: r.homeNode,
    status: r.status,
    pending: r.pending,
    entityRef: r.entityRef,
    history: (r.history || []).slice(-5),
    parentRunId: r.parentRunId,
    parentNodeId: r.parentNodeId,
    rootRunId: r.rootRunId,
    depth: r.depth,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  }
}

function buildWorkSource(board: BoardConfig, daemon: DaemonConfig): WorkSource {
  if (board.source.type === "gitlab") {
    const gl = daemon.channels.gitlab
    if (!gl?.token) throw new Error("channels.gitlab.token is required for gitlab boards")
    const agentUsernames: Record<string, string[]> = {}
    for (const mapping of gl.agentMappings) {
      if (mapping.gitlabUsernames.length) {
        agentUsernames[mapping.agentId] = mapping.gitlabUsernames
      }
    }
    return new GitLabWorkSource(gl.host, gl.token, board.source.projects, agentUsernames, (...args) => console.log("[board]", ...args))
  }
  throw new Error(`unsupported board source: ${(board.source as any).type}`)
}

// --- Audit log ---

function audit(entry: { actor: string; action: string; boardId: string; itemId?: string; payload?: unknown }): void {
  try {
    const dir = resolve(process.cwd(), ".agentx")
    if (!existsSync(dir)) mkdirSync(dir, { recursive: true })
    const line = JSON.stringify({ ts: new Date().toISOString(), ...entry }) + "\n"
    appendFileSync(resolve(dir, "board-audit.jsonl"), line)
  } catch { /* non-fatal */ }
}

function auditActor(req: IncomingMessage): string {
  const fwd = req.headers["x-forwarded-for"]
  return (Array.isArray(fwd) ? fwd[0] : fwd?.split(",")[0]) || req.socket.remoteAddress || "unknown"
}

// --- Live mesh snapshot ---
//
// Aggregates /agents + /mesh from the configured primary daemon, then fans out
// to each mesh peer to collect their /agents (running tasks included) so the
// dashboard can show a single cross-node view of what every agent is doing.

interface NodeLive {
  id: string
  name: string
  url: string
  reachable: boolean
  error?: string
  /** #193 — last good data kept while the node is briefly unreachable. */
  stale?: boolean
  staleSince?: string
  /** #193 — Claude Code sessions registered with this node's attach hooks.
   *  Loopback-only on the daemon, so only the dashboard's own host reports. */
  attachSessions?: Array<{
    session: string
    project: string
    agentIds: string[]
    mode: string
    lastSeenAt: number
    pending: number
    /** Bound, but the session has not reported since a restart or idle drop. */
    waiting?: boolean
  }>
  uptimeSec?: number
  /** Tasks a restart would cut off (GET /health; undefined on older nodes). */
  inflight?: number
  /** A "restart when idle" request the node is holding (GET /health). */
  restart?: { state: "none" | "pending" | "restarting"; requestedAt?: string; deadline?: string }
  agents: Array<{
    id: string
    name: string
    tier: string
    model?: string
    color?: string
    skillCount?: number
    active: number
    total: number
    errors: number
    lastActive?: string
    lastSummary?: { text: string; at: string; ok: boolean }
    /** #193 — the Claude Code session answering for this agent, if any. */
    attached?: { session: string; project: string; mode: string; lastSeenAt: number; waiting?: boolean }
    hourlyTasks?: number[]
    runningTasks?: Array<{
      id: string
      messagePreview: string
      channel: string
      chatId?: string
      sender?: string
      startedAt: string
    }>
  }>
  /** Today's per-agent usage rollup pulled from /health. */
  usage?: {
    date: string
    agents: Record<string, {
      tasks: number
      totalDuration: number
      errors: number
      inputTokens: number
      outputTokens: number
      cacheReadTokens: number
      cacheCreateTokens: number
      byChannel?: Record<string, { tasks: number }>
    }>
  }
  crons?: Array<{
    id: string
    enabled: boolean
    schedule: string
    timezone?: string
    agent: string
    /** "command" jobs run a shell command, never an agent — no task to open. */
    kind: "agent" | "command"
    model?: string
    nextRun?: string
    retryPending?: boolean
    consecutiveErrors: number
  }>
  cronRuns?: Array<{
    jobId: string
    startedAt: string
    completedAt: string
    duration: number
    status: "success" | "failed" | "timeout"
    responseSummary?: string
    errorSummary?: string
    isRetry: boolean
    retryAttempt: number
    taskId?: string
    rootTaskId?: string
    traceId?: string
    sessionId?: string
  }>
  /** Schedules and cron/hook workflows with staleness flags (GET /routines).
   *  Undefined when the node predates the endpoint. */
  routines?: Routine[]
  /** A live lesson on this node's screen (GET /talk), while one runs. It
   *  holds the listener's screen and voice, so it is shown with a stop. */
  lesson?: {
    agentId: string | null
    mode: string
    goal: string
    step: number
    saying: string
    startedAt: string
  }
}
interface LiveSnapshot {
  ts: string
  nodes: NodeLive[]
}

/** How long an optional snapshot endpoint may take before it's skipped. */
const OPTIONAL_ENDPOINT_MS = 2_000
/** An optional endpoint's reply, its JSON body already read. */
interface PrefetchedJson { ok: boolean; json: () => Promise<any> }

export async function fetchDaemonAgents(
  url: string,
  token?: string,
  signal?: AbortSignal,
  day?: { date: string; timezone: string },
): Promise<NodeLive> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`
  const base: NodeLive = { id: url, name: url, url, reachable: false, agents: [] }
  try {
    const cronQuery = day ? `?date=${encodeURIComponent(day.date)}&timezone=${encodeURIComponent(day.timezone)}` : ""
    // Only /agents decides whether the node is reachable. The rest are
    // extras with their own shorter deadline, so one slow route (a node
    // busy reading its cron history, #245) can't hold the snapshot past
    // the shared abort and leave /agents' body unreadable.
    // The body is read inside the same deadline, straight away: read later,
    // after the slowest extra gave up, it would already be aborted.
    const optional = (path: string): Promise<PrefetchedJson | null> => {
      const own = new AbortController()
      const timer = setTimeout(() => own.abort(), OPTIONAL_ENDPOINT_MS)
      const stop = () => own.abort()
      signal?.addEventListener("abort", stop, { once: true })
      return fetch(url + path, { headers, signal: own.signal })
        .then(async (r) => {
          const body: unknown = r.ok ? await r.json().catch(() => undefined) : undefined
          return { ok: body !== undefined, json: async () => body }
        })
        .catch(() => null)
        .finally(() => { clearTimeout(timer); signal?.removeEventListener("abort", stop) })
    }
    const [healthRes, agentsRes, meshRes, cronsRes, cronRunsRes, talkRes, routinesRes, attachRes] = await Promise.all([
      optional("/health"),
      fetch(url + "/agents", { headers, signal }).catch(() => null),
      optional("/mesh"),
      optional("/crons"),
      optional("/crons/runs" + cronQuery),
      optional("/talk"),
      optional("/routines"),
      optional("/attach/sessions"),
    ])
    if (!agentsRes || !agentsRes.ok) {
      base.error = agentsRes ? `HTTP ${agentsRes.status}` : "unreachable"
      return base
    }
    const agents: any[] = await agentsRes.json()
    base.agents = agents.map((a) => ({
      id: a.id, name: a.name, tier: a.tier, model: a.model,
      // The agent's colour (#157), for the phone app's voice orb.
      ...(typeof a.color === "string" && /^#[0-9a-fA-F]{6}$/.test(a.color) ? { color: a.color } : {}),
      skillCount: Number.isFinite(a.skillCount) ? a.skillCount : undefined,
      active: a.active || 0, total: a.total || 0, errors: a.errors || 0,
      lastActive: a.lastActive,
      lastSummary: a.lastSummary,
      hourlyTasks: Array.isArray(a.hourlyTasks) ? a.hourlyTasks : undefined,
      runningTasks: Array.isArray(a.runningTasks) ? a.runningTasks : [],
    }))
    if (healthRes && healthRes.ok) {
      const h: any = await healthRes.json().catch(() => ({}))
      base.uptimeSec = h.uptime
      if (typeof h.inflight?.total === "number") base.inflight = h.inflight.total
      if (h.restart && typeof h.restart.state === "string") base.restart = h.restart
      base.name = h.node?.name || h.node?.id || url
      base.id = h.node?.id || url
      // /health already embeds today's usage rollup — reuse it so the
      // dashboard doesn't need a separate /usage call per node.
      if (h.usage) base.usage = h.usage
    }
    if (cronsRes && cronsRes.ok) {
      const jobs: any[] = await cronsRes.json().then((j: unknown) => (Array.isArray(j) ? j : []), () => [])
      base.crons = jobs.map((job) => ({
        id: job.id,
        enabled: job.enabled === true,
        schedule: String(job.schedule || ""),
        timezone: job.timezone,
        agent: String(job.agent || ""),
        kind: typeof job.command === "string" && job.command.trim() ? "command" : "agent",
        model: job.model,
        nextRun: job.nextRun,
        retryPending: job.retryPending === true,
        consecutiveErrors: Number(job.consecutiveErrors) || 0,
      }))
    }
    if (cronRunsRes && cronRunsRes.ok) {
      const history: any = await cronRunsRes.json().catch(() => ({}))
      base.cronRuns = Array.isArray(history.runs) ? history.runs : []
    }
    // Older daemons 404 here; leaving `routines` unset tells the page the
    // node does not report them rather than that it has none.
    if (routinesRes && routinesRes.ok) {
      const body: any = await routinesRes.json().catch(() => null)
      if (body && Array.isArray(body.routines)) base.routines = body.routines.slice(0, ROUTINE_LIMITS.routines)
    }
    if (talkRes && talkRes.ok) {
      const t: any = await talkRes.json().catch(() => null)
      if (t?.active && t.kind === "lesson") {
        base.lesson = {
          agentId: t.agentId ?? null, mode: String(t.mode || "teach"), goal: String(t.goal || ""),
          step: Number(t.step) || 0, saying: String(t.saying || ""), startedAt: String(t.startedAt || ""),
        }
      }
    }
    // Remote nodes answer 403 (loopback-only), which just means no attach data.
    if (attachRes && attachRes.ok) {
      const body: any = await attachRes.json().catch(() => null)
      if (body && Array.isArray(body.sessions)) applyAttachSessions(base, body.sessions, body.saved ?? {})
    }
    base.reachable = true
    // Expose mesh peer info for discovery, but the caller does fan-out separately.
    ;(base as any)._peers = Array.isArray(meshRes?.ok ? await meshRes.json() : null) ? (base as any)._peers : undefined
  } catch (e: any) {
    base.error = e.message || String(e)
  }
  return base
}

// Lazy /admin/wiki/* handler — built once per dashboard process. The
// WikiHub it wraps reads articles from disk on every request (no
// in-memory cache), so absorbs and edits from elsewhere show up
// without a restart.
let _wikiHandler: ((req: IncomingMessage, res: ServerResponse) => Promise<void>) | null = null
function getOrCreateWikiHandler(ctx: { config: DaemonConfig; token?: string }): (req: IncomingMessage, res: ServerResponse) => Promise<void> {
  if (_wikiHandler) return _wikiHandler
  const wikiDir = resolve(process.cwd(), ".agentx/wiki")
  // Match the CLI defaults exactly so the embedded view renders the
  // same hub/sidebar/article markup as `agentx wiki serve` does — they
  // share the handler, but mode changes which `hubHome`/`hubSidebar`
  // branches fire (graph mode adds the article-list + type-label).
  _wikiHandler = createWikiHandler({
    wikiDir,
    pathPrefix: "/admin/wiki",
    mode: "graph",
    // No remote-peer browsing in the embedded view — keeps the dashboard
    // independent of mesh state. Operators who want cross-mesh wiki nav
    // can still run `agentx wiki serve --peer <url>`.
  })
  // ctx is passed for future use (auth etc.) but unused right now.
  void ctx
  return _wikiHandler
}


async function fetchMeshPeers(primaryUrl: string, token?: string, signal?: AbortSignal): Promise<Array<{ url: string; name: string; token?: string }>> {
  try {
    const headers: Record<string, string> = {}
    if (token) headers["Authorization"] = `Bearer ${token}`
    const r = await fetch(primaryUrl + "/mesh", { headers, signal })
    if (!r.ok) return []
    const peers: any[] = await r.json()
    return peers
      .filter((p) => p && p.peerUrl)
      .map((p) => ({ url: String(p.peerUrl).replace(/\/+$/, ""), name: p.peer || p.peerUrl }))
  } catch { return [] }
}

/** GET /api/mesh/feed: the primary daemon's recent events, task steps left out. */
async function serveMeshFeed(res: ServerResponse, config: DaemonConfig, q: URLSearchParams): Promise<void> {
  const limit = Math.max(1, Math.min(200, Number(q.get("limit")) || 50))
  const primary = config.dashboard.daemonUrl.replace(/\/+$/, "")
  const token = process.env.MESH_TOKEN || dashboardTokenForNode(config.dashboard, primary)
  try {
    const r = await fetch(`${primary}/events/recent?skip=task:step&limit=${limit}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: AbortSignal.timeout(5000),
    })
    if (!r.ok) { sendJson(res, 502, { error: `daemon answered ${r.status}` }); return }
    const body = await r.json() as { events?: unknown[] }
    sendJson(res, 200, { node: config.node.name || config.node.id, events: body.events ?? [] })
  } catch (e: any) { sendJson(res, 502, { error: e.message || "daemon unreachable" }) }
}

/** Every daemon this dashboard is allowed to read: the primary, the
 *  explicitly configured extras, and whatever the primary's /mesh reports.
 *  Shared by the live snapshot and the analytics fan-out so both agree on
 *  what "the fleet" means, and so the analytics drill-down proxy has the
 *  same allowlist as the task proxies. */
export async function resolveNodeTargets(daemon: DaemonConfig, signal?: AbortSignal): Promise<NodeTarget[]> {
  const dash = daemon.dashboard
  const primaryUrl = dash.daemonUrl.replace(/\/+$/, "")
  const primaryToken = dashboardTokenForNode(dash, primaryUrl)
  const seen = new Map<string, NodeTarget>()
  seen.set(primaryUrl, { name: "primary", url: primaryUrl, token: primaryToken })
  for (const d of dash.daemons) {
    const key = d.url.replace(/\/+$/, "")
    if (!seen.has(key)) seen.set(key, { name: d.name, url: key, token: dashboardTokenForNode(dash, key) })
  }
  const meshPeers = await fetchMeshPeers(primaryUrl, primaryToken, signal)
  for (const p of meshPeers) if (!seen.has(p.url)) seen.set(p.url, { ...p, token: dashboardTokenForNode(dash, p.url) })
  return [...seen.values()]
}

// #193 — each node gets its own budget: one slow daemon used to exhaust a
// shared 3 s and vanish from Live entirely.
const TARGETS_TIMEOUT_MS = 3_000
const NODE_TIMEOUT_MS = 6_000
/** How long a node's last good data is shown (marked stale) after it fails. */
const STALE_NODE_TTL_MS = 5 * 60_000
const lastGoodNode = new Map<string, { node: NodeLive; at: number }>()
const inflightSnapshots = new Map<string, Promise<LiveSnapshot>>()

/** One build at a time per view: every open Live tab and the SSE ticks share
 *  it, so a slow daemon is not hit by piled-up overlapping snapshots. */
function buildLiveSnapshot(
  daemon: DaemonConfig,
  day?: { date: string; timezone: string },
): Promise<LiveSnapshot> {
  const key = day ? `${day.date}|${day.timezone}` : "live"
  const running = inflightSnapshots.get(key)
  if (running) return running
  const build = (async () => {
    const targets = await resolveNodeTargets(daemon, AbortSignal.timeout(TARGETS_TIMEOUT_MS))
    const nodes = await Promise.all(targets.map(async (d) => {
      const node = await fetchDaemonAgents(d.url, d.token, AbortSignal.timeout(NODE_TIMEOUT_MS), day)
      return day ? node : withLastGood(d.url, node)
    }))
    return { ts: new Date().toISOString(), nodes }
  })().finally(() => inflightSnapshots.delete(key))
  inflightSnapshots.set(key, build)
  return build
}

export function withLastGood(url: string, node: NodeLive, now: number = Date.now()): NodeLive {
  if (node.reachable && !node.error) {
    lastGoodNode.set(url, { node, at: now })
    return node
  }
  const good = lastGoodNode.get(url)
  if (!good || now - good.at > STALE_NODE_TTL_MS) return node
  return { ...good.node, stale: true, staleSince: new Date(good.at).toISOString(), error: node.error }
}

/** Test-only. */
export function resetLiveCaches(): void {
  lastGoodNode.clear()
  inflightSnapshots.clear()
}

export function applyAttachSessions(
  node: NodeLive,
  sessions: Array<{ sessionId: string; cwd?: string; agentIds?: string[]; mode?: string; lastSeenAt?: number; pending?: number }>,
  saved: Record<string, { agentIds?: string[]; mode?: string; cwd?: string; savedAt?: number }>,
): void {
  const project = (cwd?: string) => (cwd ? cwd.replace(/\/+$/, "").split("/").pop() || cwd : "")
  const rows: NonNullable<NodeLive["attachSessions"]> = sessions.map((s) => ({
    session: String(s.sessionId).slice(0, 8),
    project: project(s.cwd),
    agentIds: Array.isArray(s.agentIds) ? s.agentIds : [],
    mode: String(s.mode || "notify"),
    lastSeenAt: Number(s.lastSeenAt) || 0,
    pending: Number(s.pending) || 0,
  }))
  const live = new Set(sessions.map((s) => s.sessionId))
  for (const [id, b] of Object.entries(saved)) {
    if (live.has(id) || !Array.isArray(b.agentIds) || b.agentIds.length === 0) continue
    rows.push({
      session: id.slice(0, 8), project: project(b.cwd), agentIds: b.agentIds,
      mode: String(b.mode || "notify"), lastSeenAt: Number(b.savedAt) || 0, pending: 0, waiting: true,
    })
  }
  node.attachSessions = rows
  for (const agent of node.agents) {
    const row = rows.find((r) => r.agentIds.includes(agent.id) && !r.waiting) ?? rows.find((r) => r.agentIds.includes(agent.id))
    if (row) agent.attached = { session: row.session, project: row.project, mode: row.mode, lastSeenAt: row.lastSeenAt, waiting: row.waiting }
  }
}

function startLiveStream(req: IncomingMessage, res: ServerResponse, daemon: DaemonConfig): void {
  res.writeHead(200, {
    "Content-Type": "text/event-stream",
    "Cache-Control": "no-cache",
    "Connection": "keep-alive",
    "X-Accel-Buffering": "no",
  })
  let closed = false
  const send = (ev: string, data: unknown) => {
    if (closed) return
    res.write(`event: ${ev}\ndata: ${JSON.stringify(data)}\n\n`)
  }
  let busy = false
  const tick = async () => {
    // Never stack ticks: a slow build is waited out, not multiplied.
    if (busy) return
    busy = true
    try {
      const snap = await buildLiveSnapshot(daemon)
      send("snapshot", snap)
    } catch (e: any) {
      send("error", { message: e.message })
    } finally {
      busy = false
    }
  }
  // Initial tick immediately, then every 2s.
  tick()
  const timer = setInterval(tick, 2000)
  req.on("close", () => { closed = true; clearInterval(timer); res.end() })
}

/**
 * Proxy SSE from a daemon's task-stream endpoint back to the dashboard
 * client. Whitelists the target URL against the dashboard's known nodes
 * to avoid being turned into an open SSE proxy.
 */
async function proxyTaskStream(
  req: IncomingMessage,
  res: ServerResponse,
  ctx: { config: DaemonConfig },
  nodeUrl: string,
  agentId: string,
  taskId: string,
): Promise<void> {
  const target = nodeUrl.replace(/\/+$/, "")
  const allowed = new Set<string>()
  allowed.add(ctx.config.dashboard.daemonUrl.replace(/\/+$/, ""))
  for (const d of ctx.config.dashboard.daemons) allowed.add(d.url.replace(/\/+$/, ""))
  // Also accept any known mesh peer URL discovered at snapshot time.
  try {
    const peers = await fetchMeshPeers(ctx.config.dashboard.daemonUrl.replace(/\/+$/, ""), ctx.config.dashboard.token)
    for (const p of peers) allowed.add(p.url.replace(/\/+$/, ""))
  } catch { /* best effort */ }
  if (!allowed.has(target)) {
    sendJson(res, 403, { error: "node not in dashboard allowlist", target })
    return
  }
  const tokenForNode = dashboardTokenForNode(ctx.config.dashboard, target)
  const headers: Record<string, string> = { Accept: "text/event-stream" }
  if (tokenForNode) headers["Authorization"] = `Bearer ${tokenForNode}`
  const upstreamCtl = new AbortController()
  let upstreamRes: Response
  try {
    upstreamRes = await fetch(`${target}/agents/${encodeURIComponent(agentId)}/tasks/${encodeURIComponent(taskId)}/stream`, {
      headers,
      signal: upstreamCtl.signal,
    })
  } catch (e: any) {
    sendJson(res, 502, { error: e.message || "upstream connect failed" })
    return
  }
  if (!upstreamRes.ok || !upstreamRes.body) {
    sendJson(res, upstreamRes.status || 502, { error: `upstream HTTP ${upstreamRes.status}` })
    return
  }
  res.writeHead(200, {
    "Content-Type": "text/event-stream",
    "Cache-Control": "no-cache",
    "Connection": "keep-alive",
    "X-Accel-Buffering": "no",
  })
  const reader = upstreamRes.body.getReader()
  const pump = async () => {
    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        if (value) res.write(value)
      }
    } catch { /* upstream closed */ } finally { try { res.end() } catch { /* */ } }
  }
  pump()
  req.on("close", () => { upstreamCtl.abort(); try { res.end() } catch { /* */ } })
}

/**
 * Reject the request with 401 unless the caller presents a scoped token that
 * covers *all* of the required scopes. Returns the matching record on success,
 * or null after writing the 401 (so callers can early-return).
 */
function requireScopedToken(
  req: IncomingMessage,
  res: ServerResponse,
  required: string[],
): TokenRecord | null {
  const token = extractToken(req as any)
  if (!token) {
    sendJson(res, 401, { error: "missing token", hint: "pass Authorization: Bearer <token> (agentx token create)" })
    return null
  }
  const rec = new TokenStore().verify(token)
  if (!rec) {
    sendJson(res, 401, { error: "invalid or revoked token" })
    return null
  }
  for (const s of required) {
    if (!recordHasScope(rec, s)) {
      sendJson(res, 403, { error: `token missing scope: ${s}`, scopes: rec.scopes })
      return null
    }
  }
  return rec
}

/**
 * Public agent endpoint — external apps POST a message, we forward to the
 * daemon's /task and return the final response. Only agents whose config
 * says `access: "public"` are reachable.
 */
async function proxyPublicAgentMessage(
  req: IncomingMessage,
  res: ServerResponse,
  config: DaemonConfig,
  agentId: string,
): Promise<void> {
  const agentDef = (config.agents as any)?.[agentId]
  if (!agentDef) { sendJson(res, 404, { error: "unknown agent" }); return }
  if (agentDef.access !== "public") {
    sendJson(res, 403, { error: "agent is private", hint: `set agents.${agentId}.access = "public" to expose it` })
    return
  }
  let body: any
  try { body = await readJson(req) } catch { sendJson(res, 400, { error: "invalid JSON body" }); return }
  const message = body?.message
  if (!message || typeof message !== "string") {
    sendJson(res, 400, { error: "required: { message: string, context?: {...} }" })
    return
  }
  const daemonUrl = config.dashboard.daemonUrl.replace(/\/+$/, "")
  const headers: Record<string, string> = { "Content-Type": "application/json" }
  if (config.dashboard.token) headers["Authorization"] = `Bearer ${config.dashboard.token}`
  try {
    const upstream = await fetch(`${daemonUrl}/task`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        agent: agentId,
        message,
        context: {
          channel: "public-api",
          sender: body.context?.sender || "api",
          ...(body.context || {}),
        },
      }),
    })
    const text = await upstream.text()
    res.writeHead(upstream.status, {
      "Content-Type": upstream.headers.get("content-type") || "application/json; charset=utf-8",
    })
    res.end(text)
  } catch (e: any) {
    sendJson(res, 502, { error: e.message || "upstream call failed" })
  }
}

/**
 * One-shot JSON proxy for /agents/:id/tasks (list) or /agents/:id/tasks/:tid
 * (single record). Same allowlist semantics as proxyTaskStream.
 */
async function proxyTaskHistory(
  res: ServerResponse,
  ctx: { config: DaemonConfig },
  nodeUrl: string,
  agentId: string,
  taskId: string | null,
  limit: string,
): Promise<void> {
  const target = nodeUrl.replace(/\/+$/, "")
  const allowed = new Set<string>()
  allowed.add(ctx.config.dashboard.daemonUrl.replace(/\/+$/, ""))
  for (const d of ctx.config.dashboard.daemons) allowed.add(d.url.replace(/\/+$/, ""))
  try {
    const peers = await fetchMeshPeers(ctx.config.dashboard.daemonUrl.replace(/\/+$/, ""), ctx.config.dashboard.token)
    for (const p of peers) allowed.add(p.url.replace(/\/+$/, ""))
  } catch { /* */ }
  if (!allowed.has(target)) {
    sendJson(res, 403, { error: "node not in dashboard allowlist", target })
    return
  }
  const tokenForNode = dashboardTokenForNode(ctx.config.dashboard, target)
  const headers: Record<string, string> = { Accept: "application/json" }
  if (tokenForNode) headers["Authorization"] = `Bearer ${tokenForNode}`
  const upstreamPath = taskId
    ? `/agents/${encodeURIComponent(agentId)}/tasks/${encodeURIComponent(taskId)}`
    : `/agents/${encodeURIComponent(agentId)}/tasks?limit=${encodeURIComponent(limit)}`
  try {
    const r = await fetch(`${target}${upstreamPath}`, { headers })
    const body = await r.text()
    res.writeHead(r.status, {
      "Content-Type": r.headers.get("content-type") || "application/json; charset=utf-8",
    })
    res.end(body)
  } catch (e: any) {
    sendJson(res, 502, { error: e.message || "upstream fetch failed" })
  }
}

/**
 * Cross-node POST proxy for the live page's Stop / Update controls.
 * Forwards the operator's request body verbatim to the originating daemon's
 * /api/tasks/:taskId/{cancel|followup} endpoint. Same allowlist + token
 * resolution as proxyTaskStream / proxyTaskHistory.
 */
async function proxyTaskAction(
  req: IncomingMessage,
  res: ServerResponse,
  ctx: { config: DaemonConfig },
  nodeUrl: string,
  taskId: string,
  kind: "cancel" | "followup",
): Promise<void> {
  await proxyNodePost(req, res, ctx, nodeUrl, `/api/tasks/${encodeURIComponent(taskId)}/${kind}`)
}

/** POST to a daemon the dashboard may read, with that node's token. The
 *  node must be in the allowlist, so this is never an open proxy. */
async function proxyNodePost(
  req: IncomingMessage,
  res: ServerResponse,
  ctx: { config: DaemonConfig },
  nodeUrl: string,
  upstreamPath: string,
): Promise<void> {
  let body: unknown = {}
  try { body = (await readJson(req)) ?? {} } catch { /* leave default */ }
  const r = await postToNode(ctx.config, nodeUrl, upstreamPath, body)
  res.writeHead(r.status, { "Content-Type": r.contentType })
  res.end(r.text)
}

/** POST to a daemon the dashboard may read, with that node's token. The
 *  node must be in the allowlist, so this is never an open proxy. */
async function postToNode(
  config: DaemonConfig,
  nodeUrl: string,
  upstreamPath: string,
  body: unknown,
): Promise<{ status: number; text: string; contentType: string }> {
  const asJson = (status: number, b: unknown) => ({ status, text: JSON.stringify(b), contentType: "application/json" })
  const target = nodeUrl.replace(/\/+$/, "")
  const allowed = new Set<string>()
  allowed.add(config.dashboard.daemonUrl.replace(/\/+$/, ""))
  for (const d of config.dashboard.daemons) allowed.add(d.url.replace(/\/+$/, ""))
  try {
    const peers = await fetchMeshPeers(config.dashboard.daemonUrl.replace(/\/+$/, ""), config.dashboard.token)
    for (const p of peers) allowed.add(p.url.replace(/\/+$/, ""))
  } catch { /* */ }
  if (!allowed.has(target)) return asJson(403, { error: "node not in dashboard allowlist", target })
  const tokenForNode = dashboardTokenForNode(config.dashboard, target)
  const headers: Record<string, string> = { "Content-Type": "application/json", Accept: "application/json" }
  if (tokenForNode) headers["Authorization"] = `Bearer ${tokenForNode}`
  try {
    const r = await fetch(`${target}${upstreamPath}`, { method: "POST", headers, body: JSON.stringify(body ?? {}) })
    return { status: r.status, text: await r.text(), contentType: r.headers.get("content-type") || "application/json; charset=utf-8" }
  } catch (e: any) {
    return asJson(502, { error: e.message || "upstream fetch failed" })
  }
}

/** What the phone app's Alerts tab needs. The dashboard shares the daemon's
 *  folder (as it does for tokens.json), so it opens the same SQLite file
 *  the daemon's PushAdapter sends from. */
function appPushDeps(config: DaemonConfig): AppPushDeps {
  const push = config.channels.push
  const off = (reason: string): AppPushDeps => ({ store: () => null, publicKey: () => null, keepRecent: 0, allowedHosts: [], reason })
  if (!push.enabled) return off("Notifications are off. On the computer, run: agentx notifications push --enable")
  if (push.relayTo) {
    // #711 — this node relays: the phone subscribes on the push host,
    // through this node's daemon (POST /push/app, push-bridge.ts).
    const primary = config.dashboard.daemonUrl.replace(/\/+$/, "")
    const token = process.env.MESH_TOKEN || dashboardTokenForNode(config.dashboard, primary)
    relayedPushHere()
    return {
      ...off(`Can't reach ${push.relayTo}, the computer that sends notifications`),
      async forward(call) {
        const r = await fetch(`${primary}/push/app`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ op: "app", call }),
          signal: AbortSignal.timeout(20_000),
        })
        const body = await r.json().catch(() => ({})) as { status?: unknown; body?: unknown; error?: string }
        if (!r.ok || typeof body.status !== "number") throw new Error(body.error || `the daemon answered ${r.status}`)
        return { status: body.status, body: body.body ?? {} }
      },
    }
  }
  const keysPath = pushKeysPath(push.keysFile)
  return {
    store: () => {
      const db = dashboardDb()
      return db ? new PushStore(db) : null
    },
    publicKey: () => readPushKeys(keysPath)?.publicKey ?? null,
    keepRecent: push.keepRecent,
    allowedHosts: push.allowedHosts,
    reason: "The database on this computer is unavailable, so notifications can't be saved.",
  }
}

/** Places and place reminders (#676), for the phone app and /places.
 *  Reminders are pushed through the daemon's push channel, so they are
 *  only sent where the phone's pushes are: push on here, not relayed. */
function appPlacesDeps(config: DaemonConfig): PlacesDeps {
  const push = config.channels.push
  const url = config.dashboard.daemonUrl.replace(/\/+$/, "")
  const fire = push.enabled && !push.relayTo
    ? daemonFireDeps({ url, token: dashboardTokenForNode(config.dashboard, url), operatorKey: loadOperatorKey(process.cwd()) ?? undefined })
    : null
  const reason = push.relayTo
    ? `this computer relays notifications to ${push.relayTo}; place reminders work on a phone paired with ${push.relayTo}.`
    : appPushDeps(config).reason
  return placesDeps(config, process.cwd(), fire, reason)
}

/** What the phone app's Fleet and Activity tabs read and act through
 *  (app-fleet.ts). Built per request, like every other handler here, so a
 *  config reload is picked up without restarting the dashboard. */
function appFleetDeps(config: DaemonConfig): AppFleetDeps {
  const primary = config.dashboard.daemonUrl.replace(/\/+$/, "")
  const parse = (text: string) => { try { return JSON.parse(text) } catch { return { error: text.slice(0, 200) } } }
  return {
    snapshot: (day) => buildLiveSnapshot(config, day),
    async nodePost(nodeUrl, path, body) {
      const r = await postToNode(config, nodeUrl, path, body)
      return { status: r.status, body: parse(r.text) }
    },
    async approvals() {
      const local: NodeApprovals = { node: primary, nodeName: config.node?.name || "this computer", items: [] }
      try {
        local.items = listInbox({ root: process.cwd() }).items.map(toApprovalItem)
      } catch (e: any) { local.error = e.message }
      const remote = await Promise.all(config.dashboard.daemons.map(async (d): Promise<NodeApprovals> => {
        const out: NodeApprovals = { node: d.url.replace(/\/+$/, ""), nodeName: d.name, items: [] }
        const peer = findPeer(d.url, config)
        if (!peer) return out
        try {
          const r = await fetch(`${peer.url}/api/admin/approvals`, {
            headers: { "X-Agentx-Peer": "primary", ...(peer.token ? { Authorization: `Bearer ${peer.token}` } : {}) },
            signal: AbortSignal.timeout(4000),
          })
          if (!r.ok) { out.error = `HTTP ${r.status}`; return out }
          const body = await r.json() as { items?: InboxItem[] }
          out.items = (body.items ?? []).map(toApprovalItem)
        } catch (e: any) { out.error = e.message || "unreachable" }
        return out
      }))
      return [local, ...remote]
    },
    async decide(nodeUrl, key, action, by, answer = {}) {
      const target = nodeUrl.replace(/\/+$/, "")
      if (target === primary) {
        const settings = readApprovalSettings()
        const r = await decide({ root: process.cwd() }, key, action, { laterHours: settings.laterHours, by, ...answer })
        return r.ok ? { status: 200, body: { ok: true, message: r.message } } : { status: 409, body: { error: r.error } }
      }
      const peer = findPeer(target, config)
      if (!peer) return { status: 403, body: { error: "node not in dashboard allowlist", target } }
      try {
        const r = await fetch(`${peer.url}/api/admin/approvals/decide`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "agentx-board",
            "X-Agentx-Peer": "primary",
            ...(peer.token ? { Authorization: `Bearer ${peer.token}` } : {}),
          },
          body: JSON.stringify({ key, action, ...answer }),
          signal: AbortSignal.timeout(10000),
        })
        return { status: r.status, body: parse(await r.text()) }
      } catch (e: any) {
        return { status: 502, body: { error: e.message || "peer dashboard unreachable" } }
      }
    },
  }
}

/** What the phone app's Chat tab talks through (app-chat.ts): turns go to
 *  the primary daemon with its token, the picker reads the live snapshot,
 *  and conversations live in the same SQLite file as the push tables. */
/** One member store per dashboard process, so its hourly log pruning holds;
 *  a new one when the retention setting changes. */
let _members: { days: number; store: MemberStore } | null = null
function membersStore(retentionDays: number): MemberStore {
  if (_members?.days !== retentionDays) _members = { days: retentionDays, store: new MemberStore(process.cwd(), Date.now, retentionDays) }
  return _members!.store
}

function appChatDeps(config: DaemonConfig): AppChatDeps {
  const url = config.dashboard.daemonUrl.replace(/\/+$/, "")
  const token = dashboardTokenForNode(config.dashboard, url)
  const fleet = appFleetDeps(config)
  return {
    store: () => {
      const db = dashboardDb()
      return db ? new AppChatStore(db) : null
    },
    // The operator key proves to the daemon that a phone turn is the
    // owner's (#393). Read here, next to agentx.json; the daemon creates it.
    daemon: { url, token, name: config.node?.name, operatorKey: loadOperatorKey(process.cwd()) ?? undefined },
    snapshot: () => buildLiveSnapshot(config),
    meshPeers: async () => {
      const r = await fetch(url + "/mesh", { headers: token ? { Authorization: `Bearer ${token}` } : {}, signal: AbortSignal.timeout(3000) })
      return r.ok ? (await r.json()) as AppMeshPeer[] : []
    },
    nodePost: (nodeUrl, path, body) => fleet.nodePost(nodeUrl, path, body),
    tokenFor: (nodeUrl) => dashboardTokenForNode(config.dashboard, nodeUrl.replace(/\/+$/, "")),
    ...finishAlertDeps(config, url, token),
  }
}

/** Finish notifications for the phone app (#265). The push channel runs in
 *  the daemon, so the dashboard asks it through POST /channel/send with the
 *  phone's device id as the chat id: that addresses one phone, never all.
 *  Off when push is off here, when the phone has no subscription, or when
 *  it turned the setting off in Alerts. On a relaying node the daemon
 *  forwards it to the host, which finds the phone by this node's name. */
function finishAlertDeps(config: DaemonConfig, url: string, token: string | undefined): Pick<AppChatDeps, "finishAlerts" | "notifyFinish"> {
  const pushDeps = appPushDeps(config)
  return {
    finishAlerts: (deviceId) => {
      // A relay asks the host's answer it last passed on (#711).
      if (pushDeps.forward) return relayedPush.finishOn(deviceId)
      const store = pushDeps.store()
      return !!store && store.chatFinishOn(deviceId) && store.list(deviceId).length > 0
    },
    notifyFinish: async (p) => {
      try {
        const r = await fetch(url + "/channel/send", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          // First line is the title (splitTitle in channels/ntfy.ts); the
          // one button's link is where a tap on the notification lands.
          body: JSON.stringify({ channel: "push", chatId: p.deviceId, text: `${p.title}\n${p.body}`, buttons: [{ label: "Open", url: p.url }] }),
          signal: AbortSignal.timeout(10_000),
        })
        if (!r.ok) console.error(`[app] finish notification not sent: HTTP ${r.status} ${(await r.text()).slice(0, 160)}`)
      } catch (e: any) {
        console.error(`[app] finish notification not sent: ${e?.message || e}`)
      }
    },
  }
}

/** Follows the primary daemon's delegation events and files each phone
 *  reply in its conversation. Reads replies from the node that published
 *  them, with the token the dashboard holds for it. */
function appCallbackPuller(config: DaemonConfig): AppCallbackPuller {
  const primary = config.dashboard.daemonUrl.replace(/\/+$/, "")
  const tokenFor = (url: string) => dashboardTokenForNode(config.dashboard, url.replace(/\/+$/, ""))
  const auth = (url: string): Record<string, string> => {
    const t = tokenFor(url)
    return t ? { Authorization: `Bearer ${t}` } : {}
  }
  const chat = appChatDeps(config)
  return new AppCallbackPuller({
    recent: async (since, limit) => {
      const q = new URLSearchParams({ kind: "delegation", limit: String(limit) })
      if (since) q.set("since", since)
      const r = await fetch(`${primary}/events/recent?${q}`, { headers: auth(primary), signal: AbortSignal.timeout(5000) })
      if (!r.ok) throw new Error(`the daemon answered ${r.status}`)
      const body = await r.json() as { events?: CallbackEvent[]; gap?: boolean }
      return { events: Array.isArray(body.events) ? body.events : [], gap: body.gap === true }
    },
    nodeUrl: async (node) => {
      const snap = await buildLiveSnapshot(config)
      const n = snap.nodes.find((x) => x.name === node || x.id === node)
      return n?.url ? n.url.replace(/\/+$/, "") : null
    },
    conversationUrl: async (conv) => {
      if (conv.node === "local") return primary
      const peer = (await chat.meshPeers().catch(() => [] as AppMeshPeer[])).find((p) => p.peer === conv.node)
      return peer ? peer.peerUrl.replace(/\/+$/, "") : null
    },
    fetchReply: async (url, taskId) => {
      const r = await fetch(`${url}/a2a/delegations/${encodeURIComponent(taskId)}/reply`, { headers: auth(url), signal: AbortSignal.timeout(5000) })
      if (r.status === 404) return null
      if (!r.ok) throw new Error(`the node answered ${r.status}`)
      return await r.json() as CallbackReplyBody
    },
    store: chat.store,
    presence: appPresence(),
    finishAlerts: chat.finishAlerts,
    notifyFinish: chat.notifyFinish,
    log: (m) => console.log(m),
    warn: (m) => console.warn(m),
  })
}

/** What the phone app's voice routes forward to (app-voice.ts): the same
 *  daemon and token as Chat, and the conversations a spoken answer is
 *  checked against. */
function appVoiceDeps(config: DaemonConfig): AppVoiceDeps {
  const url = config.dashboard.daemonUrl.replace(/\/+$/, "")
  return {
    store: () => {
      const db = dashboardDb()
      return db ? new AppChatStore(db) : null
    },
    daemon: { url, token: dashboardTokenForNode(config.dashboard, url) },
  }
}

function appCameraDeps(config: DaemonConfig): AppCameraDeps {
  const url = config.dashboard.daemonUrl.replace(/\/+$/, "")
  return { daemon: { url, token: dashboardTokenForNode(config.dashboard, url) }, nodeName: config.node.name }
}

function toApprovalItem(i: InboxItem): ApprovalItem {
  return {
    key: i.key,
    title: i.title,
    ask: i.ask,
    recommend: i.recommend,
    yes: i.yes,
    no: i.no,
    raisedBy: i.raised_by,
    expires: i.expires,
    ...(i.choices ? { choices: i.choices } : {}),
    ...(i.draft ? { draft: i.draft } : {}),
  }
}

// --- A2A issue drafting ---
//
// Builds a convention-aware prompt, calls the configured agent on the daemon,
// and parses a strict JSON reply. The agent returns title + refined description
// + suggested labels + (optional) suggested assignee username.

const DRAFT_CONVENTIONS = `
You are helping draft a new GitLab issue. Follow these conventions strictly.

Title pattern:
  <Kind>: <page|component> – <short symptom>
Kinds: Bug, UX, Copy, Perf, A11y, Security, UI, Incident (defects)
       Feat, Enh, Deprecate (features)
       Epic, Story, Spike, Req (planning)
       Data, Env, Test, Refactor, Chore, Task, Mgmt (technical)

Title guidance:
  - Be specific about the location ("Contact page", not "frontend").
  - Use lowercase for the symptom.
  - Keep the symptom 4-7 words max, present tense.
  - Do NOT include issue IDs.

Description structure (Markdown):
  For bugs: Problem / Steps to Reproduce / Expected / Actual / Environment / Impact.
  For features/stories: User Story (As a … I want … So that …) + Acceptance Criteria bullets.
  Otherwise: concise problem statement + any relevant context.

Labels:
  - Pick ONE Role label from: Dev, Design, QA, Ops, PM, mgmt.
  - Pick ONE Kind label matching the title prefix (bug, task, ux, ui, perf, security, copy, enhancement, …).
  - Pick ONE Priority: critical, high, medium, low.
  - Pick ONE Difficulty: diff-XS, diff-S, diff-M, diff-L, diff-XL.
  - Optional Area/feature labels if clearly applicable from the known label list.

Return ONLY a single JSON object on one line — no prose, no code fences, no explanation.
Shape:
  { "title": string, "description": string, "labels": string[], "assigneeUsername": string | null, "kind": string }
If you don't have enough information to pick a label confidently, omit it rather than guessing.
`

async function draftIssue(daemon: DaemonConfig, body: any): Promise<{ title: string; description: string; labels: string[]; assigneeUsername?: string; kind?: string; raw?: string }> {
  const rough: string = (body?.rough || "").toString().trim()
  if (!rough) throw new Error("rough description required")
  const agentId: string | undefined = body?.agent || daemon.dashboard.draftAgent
  if (!agentId) throw new Error("no draft agent configured (set dashboard.draftAgent or pass agent in body)")

  const boardCtx: {
    boardName?: string; primaryLabel?: string; project?: string;
    knownLabels?: string[]; members?: string[]; columnLabel?: string
  } = body?.context || {}

  const userPrompt = [
    `Rough description from the user:`,
    `"""`,
    rough,
    `"""`,
    ``,
    `Board: ${boardCtx.boardName || "(unspecified)"}`,
    boardCtx.primaryLabel ? `Primary board label (include in labels): ${boardCtx.primaryLabel}` : "",
    boardCtx.columnLabel ? `Target column label (include in labels): ${boardCtx.columnLabel}` : "",
    boardCtx.project ? `GitLab project: ${boardCtx.project}` : "",
    boardCtx.knownLabels?.length ? `Known labels in this project (prefer picking from these when they fit):\n- ${boardCtx.knownLabels.slice(0, 60).join("\n- ")}` : "",
    boardCtx.members?.length ? `Possible assignees (GitLab usernames): ${boardCtx.members.slice(0, 20).join(", ")}` : "",
    ``,
    `Produce the JSON draft now.`,
  ].filter(Boolean).join("\n")

  const url = daemon.dashboard.daemonUrl.replace(/\/+$/, "") + "/task"
  const headers: Record<string, string> = { "Content-Type": "application/json" }
  if (daemon.dashboard.token) headers["Authorization"] = `Bearer ${daemon.dashboard.token}`

  const r = await fetch(url, {
    method: "POST", headers,
    body: JSON.stringify({
      agent: agentId,
      message: userPrompt,
      context: { channel: "a2a", sender: "board-dashboard", systemPrompt: DRAFT_CONVENTIONS },
    }),
  })
  if (!r.ok) throw new Error(`daemon /task HTTP ${r.status}: ${await r.text().catch(() => "")}`)
  const data: any = await r.json()
  if (data.error) throw new Error(String(data.error))
  const content: string = (data.content || "").toString()

  // Agent may wrap JSON in ```json fences or prose; extract the first balanced {...}.
  const parsed = extractJson(content)
  if (!parsed || typeof parsed.title !== "string") {
    throw new Error("agent did not return a valid JSON draft. Raw: " + content.slice(0, 400))
  }
  const labels: string[] = Array.isArray(parsed.labels) ? parsed.labels.filter((x: any) => typeof x === "string") : []
  // Inject primary/column labels if the agent missed them.
  if (boardCtx.primaryLabel && !labels.includes(boardCtx.primaryLabel)) labels.unshift(boardCtx.primaryLabel)
  if (boardCtx.columnLabel && !labels.includes(boardCtx.columnLabel)) labels.unshift(boardCtx.columnLabel)
  return {
    title: parsed.title.trim(),
    description: (parsed.description || "").toString(),
    labels,
    assigneeUsername: typeof parsed.assigneeUsername === "string" ? parsed.assigneeUsername : undefined,
    kind: typeof parsed.kind === "string" ? parsed.kind : undefined,
    raw: content,
  }
}

function extractJson(text: string): any {
  if (!text) return null
  // Try direct parse
  try { return JSON.parse(text) } catch { /* fallthrough */ }
  // Strip code fences
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/)
  if (fenced) { try { return JSON.parse(fenced[1]) } catch { /* fallthrough */ } }
  // Find first balanced {...}
  const start = text.indexOf("{")
  if (start === -1) return null
  let depth = 0
  for (let i = start; i < text.length; i++) {
    const c = text[i]
    if (c === "{") depth++
    else if (c === "}") { depth--; if (depth === 0) { try { return JSON.parse(text.slice(start, i + 1)) } catch { return null } } }
  }
  return null
}

// --- HTTP helpers ---

/** Resolve a path relative to THIS module's directory at runtime.  After
 *  tsup bundles dist/board-dashboard-*.js is placed in dist/, so
 *  `../../dist/web/foo.js` is relative to wherever the bundled module
 *  actually runs. `fileURLToPath(import.meta.url)` is the ESM equivalent
 *  of __filename. */
function resolveFromHere(rel: string): string {
  const here = dirname(fileURLToPath(import.meta.url))
  return resolve(here, rel)
}

async function readAsset(path: string): Promise<Buffer> {
  return readFile(path)
}

function sendJson(res: ServerResponse, status: number, body: unknown): void {
  res.writeHead(status, { "Content-Type": "application/json; charset=utf-8" })
  res.end(JSON.stringify(body))
}

async function readJson(req: IncomingMessage): Promise<any> {
  return new Promise((resolve, reject) => {
    const chunks: Buffer[] = []
    req.on("data", (c) => chunks.push(c))
    req.on("end", () => {
      try {
        const raw = Buffer.concat(chunks).toString("utf-8")
        resolve(raw ? JSON.parse(raw) : {})
      } catch (e) { reject(e) }
    })
    req.on("error", reject)
  })
}

// --- HTML ---


// --- Live full-screen page (/live) ---



/**
 * Derive a peer's board-dashboard URL from a dashboard.daemons[] entry.
 * Uses the explicit dashboardUrl override if set; otherwise swaps the
 * daemon's port (18800 / 19900 / 18810) for :4202, matching our convention
 * that the dashboard runs alongside the daemon.
 */
function resolvePeerDashboardUrl(d: { url: string; dashboardUrl?: string }): string {
  if (d.dashboardUrl) return d.dashboardUrl.replace(/\/+$/, "")
  return d.url.replace(/\/+$/, "").replace(/:(?:1[89][89]00|18810)$/, ":4202")
}

/**
 * Build the peers list shown in the topbar mesh selector. Combines the
 * primary daemon + any `dashboard.daemons[]` entries.
 */
function buildTopbarPeers(config: DaemonConfig, ctxConfig?: DaemonConfig): TopbarPeer[] {
  const cfg = ctxConfig || config
  const primary: TopbarPeer = {
    id: "primary",
    name: cfg.node?.name || "this daemon",
    dashboardUrl: "",  // empty = stay on current origin
    primary: true,
  }
  const extras: TopbarPeer[] = (cfg.dashboard?.daemons || []).map((d) => ({
    id: d.url.replace(/\/+$/, ""),
    name: d.name,
    dashboardUrl: resolvePeerDashboardUrl(d),
    tokenScope: (d.dashboardToken || d.token) ? "proxy-ready" : undefined,
  }))
  // Fleet pseudo-peer: only meaningful when there's at least one
  // remote peer to merge with. Placed at the bottom so users tend to
  // notice it after they've used the per-peer entries first.
  const fleet: TopbarPeer[] = extras.length > 0
    ? [{
        id: "fleet",
        name: "Fleet (all peers)",
        dashboardUrl: "",
        tokenScope: "merged across nodes",
      }]
    : []
  return [primary, ...extras, ...fleet]
}

/**
 * Forward the entire incoming admin request to the selected peer's
 * dashboard. Copies method, query string, body, and Content-Type. Auth
 * replaces anything the browser sent with the peer's configured token so
 * the local dashboard.token (if any) doesn't leak across nodes.
 */
/** Fleet-mode activity-graph snapshot: build the local snapshot in-process,
 *  fetch every configured peer's snapshot via its dashboard API, merge.
 *  Per-peer failures don't fail the whole call — they're just dropped from
 *  the merge and logged, so a single dead peer doesn't black out the view. */
async function handleActivityGraphFleet(
  req: IncomingMessage,
  res: ServerResponse,
  ctx: { config: DaemonConfig; token?: string },
): Promise<void> {
  const url = new URL(req.url || "/", "http://_")
  const hours = parseInt(url.searchParams.get("hours") || "6", 10) || 6
  sendJson(res, 200, await withForgeStatus(await buildFleetActivitySnapshot(hours, ctx.config)))
}

async function buildFleetActivitySnapshot(hours: number, config: DaemonConfig): Promise<FleetSnapshot> {
  const localNodeId = config.node?.id || config.node?.name || "local"

  const parts: Array<{ nodeId: string; snap: FleetSnapshot }> = []

  // Local node: in-process call, no HTTP round-trip.
  const localSnap = buildLocalActivityGraphSnapshot(hours)
  if (localSnap) parts.push({ nodeId: localNodeId, snap: localSnap })

  // Each configured peer: HTTP fetch via the existing per-peer proxy logic.
  // Fire in parallel; whichever peers respond on time get merged in.
  const peerEntries = (config.dashboard?.daemons || []).filter((d) => d.url)
  const peerParts: Array<{ nodeId: string; snap: FleetSnapshot } | null> = await Promise.all(peerEntries.map(async (d) => {
    const peer = findPeer(d.url.replace(/\/+$/, ""), config)
    if (!peer) return null
    try {
      const ac = new AbortController()
      const timer = setTimeout(() => ac.abort(), 8000)
      try {
        const r = await fetch(`${peer.url}/api/admin/activity-graph?hours=${hours}`, {
          headers: {
            "Accept": "application/json",
            "X-Agentx-Peer": "primary",
            ...(peer.token ? { Authorization: `Bearer ${peer.token}` } : {}),
          },
          signal: ac.signal,
        })
        if (!r.ok) return null
        return { nodeId: d.name || d.url, snap: await r.json() as FleetSnapshot }
      } finally {
        clearTimeout(timer)
      }
    } catch {
      // Drop this peer from the merge; the rest of the snapshot remains usable.
      return null
    }
  }))
  // Keep local first: the merger reports parts[0] as the serving node.
  for (const p of peerParts) if (p) parts.push(p)

  return mergeFleetSnapshots(parts)
}

/** SSE-stream the daemon's stdout to the dashboard /admin/health Logs tab.
 *  Picks the best available source: prefers `journalctl -u agentx -f`
 *  (systemd-managed nodes like peer-server), falls back to `tail -F` on
 *  /tmp/agentx-daemon.log (Mac dev launches), or the tail of the
 *  workspace pid log if neither exists. Mirrors the source the
 *  `agentx daemon logs -f` CLI hits, so both surfaces show the same
 *  data.
 *
 *  The spawned process inherits the dashboard's permissions; on
 *  peer-server `peer` already has read access to its agentx unit's
 *  journal via the systemd-journal group (no sudo needed). On Mac the
 *  log file is world-readable. */
async function streamDaemonLogs(req: IncomingMessage, res: ServerResponse): Promise<void> {
  const { spawn, execSync } = await import("child_process")

  // Prefer journalctl when available — it captures stdout from systemd,
  // which is where the daemon's logs actually land on prod. Fall back to
  // the legacy /tmp file the CLI uses.
  let cmd: string
  let args: string[]
  try {
    execSync("which journalctl", { stdio: "pipe" })
    cmd = "journalctl"
    args = ["-u", "agentx", "--no-pager", "-n", "200", "-f", "-o", "cat"]
  } catch {
    // Mac (launchd) logs to ~/.agentx/logs/daemon-stdout.log; the legacy
    // /tmp/agentx-daemon.log is the foreground-launch path. Probe both.
    const home = process.env.HOME || ""
    const candidates = [
      home && `${home}/.agentx/logs/daemon-stdout.log`,
      "/tmp/agentx-daemon.log",
    ].filter(Boolean) as string[]
    const file = candidates.find((f) => existsSync(f))
    if (!file) {
      res.writeHead(503, { "Content-Type": "application/json" })
      res.end(JSON.stringify({
        error: "no log source — install journalctl, run the daemon under launchd, or run it foreground writing to /tmp/agentx-daemon.log",
        searched: candidates,
      }))
      return
    }
    cmd = "tail"
    args = ["-F", "-n", "200", file]
  }

  res.writeHead(200, {
    "Content-Type": "text/event-stream",
    "Cache-Control": "no-cache, no-transform",
    "Connection": "keep-alive",
    "X-Accel-Buffering": "no",
  })
  res.write(`: source=${cmd}\n\n`)

  const child = spawn(cmd, args, { stdio: ["ignore", "pipe", "pipe"] })
  let buffer = ""
  const onData = (chunk: Buffer) => {
    buffer += chunk.toString("utf8")
    let idx
    while ((idx = buffer.indexOf("\n")) >= 0) {
      const line = buffer.slice(0, idx).replace(/\r$/, "")
      buffer = buffer.slice(idx + 1)
      // SSE: each line gets its own `event: line` so the client can
      // listen by name. Escape newlines defensively (lines should not
      // contain any since we split on them, but be safe).
      try { res.write(`event: line\ndata: ${line.replace(/\n/g, " ")}\n\n`) } catch { /* */ }
    }
  }
  child.stdout?.on("data", onData)
  // Mirror stderr too so the operator sees crash output.
  child.stderr?.on("data", onData)

  // Heartbeat keeps middleboxes from idling the connection out.
  const hb = setInterval(() => {
    try { res.write(`: hb\n\n`) } catch { /* */ }
  }, 15_000)

  const cleanup = () => {
    clearInterval(hb)
    try { child.kill() } catch { /* */ }
    try { res.end() } catch { /* */ }
  }
  req.on("close", cleanup)
  child.on("exit", cleanup)
  child.on("error", cleanup)
}

async function proxyAdminToPeer(
  req: IncomingMessage,
  res: ServerResponse,
  peer: { url: string; token?: string },
  pathWithQuery: string,
): Promise<void> {
  const target = peer.url + pathWithQuery
  // Read the incoming body (if any). For GET/HEAD there's no body to drain.
  const method = (req.method || "GET").toUpperCase()
  const hasBody = method !== "GET" && method !== "HEAD"
  const body: Buffer | undefined = hasBody
    ? await new Promise<Buffer>((resolve, reject) => {
        const chunks: Buffer[] = []
        req.on("data", (c: Buffer) => chunks.push(c))
        req.on("end", () => resolve(Buffer.concat(chunks)))
        req.on("error", reject)
      })
    : undefined

  const headers: Record<string, string> = {}
  const ct = req.headers["content-type"]
  if (ct) headers["Content-Type"] = Array.isArray(ct) ? ct[0] : ct
  const xrw = req.headers["x-requested-with"]
  if (xrw) headers["X-Requested-With"] = Array.isArray(xrw) ? xrw[0] : xrw
  if (peer.token) headers["Authorization"] = `Bearer ${peer.token}`
  // Tell the peer not to recurse — belt-and-braces guard.
  headers["X-Agentx-Peer"] = "primary"

  try {
    const upstream = await fetch(target, {
      method,
      headers,
      body: body && body.length > 0 ? body : undefined,
    })
    const respBody = Buffer.from(await upstream.arrayBuffer())
    res.writeHead(upstream.status, {
      "Content-Type": upstream.headers.get("content-type") || "application/json",
      "Cache-Control": "no-store",
    })
    res.end(respBody)
  } catch (e: any) {
    sendJson(res, 502, { error: `proxy to peer failed: ${e?.message || e}`, target })
  }
}

/**
 * Look up a peer by id (matches `dashboard.daemons[].url` normalised) so
 * the proxy middleware knows where to forward + what token to use.
 */
function findPeer(id: string, config: DaemonConfig): { url: string; token?: string } | null {
  const peerId = id.replace(/\/+$/, "")
  const match = (config.dashboard?.daemons || []).find((d) => d.url.replace(/\/+$/, "") === peerId)
  if (!match) return null
  return {
    url: resolvePeerDashboardUrl(match),
    token: match.dashboardToken || match.token,
  }
}

/**
 * Cross-surface theme switcher. Reads persisted theme from localStorage BEFORE
 * first paint (dropped into <head> to avoid FOUC), then wires any segmented
 * control with [data-theme-opt="..."] buttons once the DOM is ready.
 */


